import React, { createContext, useContext, useReducer, useEffect, useState, useCallback, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Link, useNavigate, useParams, useLocation } from 'react-router-dom';
import {
  Search,
  ShoppingCart,
  User,
  Home,
  Info,
  Mail,
  Settings,
  Eye,
  EyeOff,
  Star,
  DollarSign,
  Package,
  Bell,
  LogOut,
  BarChart3,
  ChevronRight,
  Zap,
  Shield,
  Users,
  Award,
  ArrowRight,
  CheckCircle,
  TrendingUp,
  MapPin,
  Phone,
  X,
  XCircle,
  Menu,
  Layers,
  Gem,
  ShoppingBag,
  Hexagon,
  Link2
} from 'lucide-react';
import { auth, db } from './firebase';
import { collection, getDocs, addDoc, doc, setDoc, query, where, getDoc, orderBy, limit as firestoreLimit } from "firebase/firestore";
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  sendEmailVerification
} from "firebase/auth";
import { v4 as uuidv4 } from 'uuid';
import emailjs from 'emailjs-com';
import { SpeedInsights } from '@vercel/speed-insights/react';
import localProductsData from './data/products_db.json';

const WHATSAPP_NUMBER = '923374855171';
const ADMIN_EMAIL = 'muhamed.ahmed100@gmail.com';

// Simple Policy Pages
const PrivacyPolicyPage = () => (
  <div className="min-h-screen bg-neutral-950 text-white pt-40 pb-12 relative overflow-hidden">
    <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iODAiIGhlaWdodD0iODAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI4MCIgaGVpZ2h0PSI4MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAwIDQwIEwgODAgNDAgTSA0MCAwIEwgNDAgODAiIGZpbGw9Im5vbmUiIHN0cm9rZT0icmdiYSgyNTUsMjU1LDI1NSwwLjAzKSIgc3Ryb2tlLXdpZHRoPSIxIi8+PC9wYXR0ZXJuPjwvZGVmcz48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSJ1cmwoI2dyaWQpIi8+PC9zdmc+')] opacity-50"></div>
    <div className="max-w-4xl mx-auto px-4 py-12 text-white-text relative z-10">
      <h1 className="text-3xl font-bold mb-6">Privacy Policy</h1>
      <p className="text-medium-gray-text mb-4">Last updated: {new Date().toISOString().slice(0, 10)}</p>
      <p className="mb-4">This Privacy Policy explains how we collect, use, and protect your information when you use our website.</p>
      <h2 className="text-2xl font-semibold mt-8 mb-3">Information We Collect</h2>
      <ul className="list-disc ml-6 space-y-2 text-medium-gray-text">
        <li>Account data: email and password (hashed by Firebase Authentication).</li>
        <li>Profile and purchase data: records in our Firestore collections `users`, `products`, and `purchases`.</li>
        <li>Usage data: basic analytics, device and browser metadata, IP address.</li>
        <li>Cookies: used for authentication and preferences.</li>
        <li>Payments: processed by PayFast. We do not store full card details.</li>
      </ul>
      <h2 className="text-2xl font-semibold mt-8 mb-3">How We Use Information</h2>
      <ul className="list-disc ml-6 space-y-2 text-medium-gray-text">
        <li>Provide access to products and complete purchases.</li>
        <li>Process payments securely via PayFast and reconcile orders.</li>
        <li>Improve our services, prevent fraud, and comply with legal requirements.</li>
        <li>Communicate about orders, updates, and support.</li>
      </ul>
      <h2 className="text-2xl font-semibold mt-8 mb-3">Sharing</h2>
      <ul className="list-disc ml-6 space-y-2 text-medium-gray-text">
        <li>Payment processors (PayFast): we share only necessary transaction details.</li>
        <li>Service providers: hosting, email, and analytics under confidentiality.</li>
        <li>Legal: when required by law or to protect rights and safety.</li>
      </ul>
      <h2 className="text-2xl font-semibold mt-8 mb-3">Security</h2>
      <p className="text-medium-gray-text">We use HTTPS, access controls, and industry-standard safeguards. See our Security Policy for details.</p>
      <h2 className="text-2xl font-semibold mt-8 mb-3">Your Rights</h2>
      <p className="text-medium-gray-text">Contact us at info@productpulse.com to access, correct, or delete your data where applicable.</p>
      <h2 className="text-2xl font-semibold mt-8 mb-3">Contact</h2>
      <p className="text-medium-gray-text">Email: info@productpulse.com</p>
    </div>
  </div>
);

const SecurityPolicyPage = () => (
  <div className="min-h-screen bg-neutral-950 text-white pt-40 pb-12 relative overflow-hidden">
    <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iODAiIGhlaWdodD0iODAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI4MCIgaGVpZ2h0PSI4MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAwIDQwIEwgODAgNDAgTSA0MCAwIEwgNDAgODAiIGZpbGw9Im5vbmUiIHN0cm9rZT0icmdiYSgyNTUsMjU1LDI1NSwwLjAzKSIgc3Ryb2tlLXdpZHRoPSIxIi8+PC9wYXR0ZXJuPjwvZGVmcz48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSJ1cmwoI2dyaWQpIi8+PC9zdmc+')] opacity-50"></div>
    <div className="max-w-4xl mx-auto px-4 py-12 text-white-text relative z-10">
      <h1 className="text-3xl font-bold mb-6">Security Policy</h1>
      <p className="text-medium-gray-text mb-4">Last updated: {new Date().toISOString().slice(0, 10)}</p>
      <h2 className="text-2xl font-semibold mt-8 mb-3">Overview</h2>
      <p className="text-medium-gray-text">We protect customer information and transactions with layered technical and organizational controls.</p>
      <h2 className="text-2xl font-semibold mt-8 mb-3">Payments via PayFast</h2>
      <ul className="list-disc ml-6 space-y-2 text-medium-gray-text">
        <li>Payments are processed on PayFast’s hosted pages; we never store card details.</li>
        <li>Server-side ITN (Instant Transaction Notification) verification and signature checks.</li>
        <li>Order amount validation and idempotent fulfillment.</li>
      </ul>
      <h2 className="text-2xl font-semibold mt-8 mb-3">Application and Data Security</h2>
      <ul className="list-disc ml-6 space-y-2 text-medium-gray-text">
        <li>HTTPS/TLS enforced; secrets not exposed in client code.</li>
        <li>Access control: least privilege for production systems.</li>
        <li>Regular updates, dependency monitoring, and secure coding practices.</li>
        <li>Backups, monitoring, and incident response procedures.</li>
      </ul>
      <h2 className="text-2xl font-semibold mt-8 mb-3">Contact</h2>
      <p className="text-medium-gray-text">Security concerns: security@productpulse.com</p>
    </div>
  </div>
);

const TermsPage = () => (
  <div className="min-h-screen bg-neutral-950 text-white pt-40 pb-12">
    <div className="max-w-4xl mx-auto px-4 py-12 text-white-text">
      <h1 className="text-3xl font-bold mb-6">Terms and Conditions</h1>
      <p className="text-medium-gray-text mb-4">Last updated: {new Date().toISOString().slice(0, 10)}</p>
      <h2 className="text-2xl font-semibold mt-8 mb-3">Agreement</h2>
      <p className="text-medium-gray-text">By accessing or using this website, you agree to these Terms. If you do not agree, please discontinue use.</p>
      <h2 className="text-2xl font-semibold mt-8 mb-3">Accounts</h2>
      <p className="text-medium-gray-text">You must provide accurate information and maintain the security of your account. You are responsible for all activity under your account.</p>
      <h2 className="text-2xl font-semibold mt-8 mb-3">Purchases</h2>
      <p className="text-medium-gray-text">Orders are subject to acceptance and availability. Payments are processed via PayFast. Fulfillment occurs after verified payment.</p>
      <h2 className="text-2xl font-semibold mt-8 mb-3">Intellectual Property</h2>
      <p className="text-medium-gray-text">All content, trademarks, and materials are owned by us or our licensors and protected by law.</p>
      <h2 className="text-2xl font-semibold mt-8 mb-3">Limitation of Liability</h2>
      <p className="text-medium-gray-text">To the maximum extent permitted by law, we are not liable for indirect or consequential damages.</p>
      <h2 className="text-2xl font-semibold mt-8 mb-3">Contact</h2>
      <p className="text-medium-gray-text">Email: info@productpulse.com</p>
    </div>
  </div>
);

const RefundPolicyPage = () => (
  <div className="min-h-screen bg-neutral-950 text-white pt-40 pb-12">
    <div className="max-w-4xl mx-auto px-4 py-12 text-white-text">
      <h1 className="text-3xl font-bold mb-6">Refund and Returns Policy</h1>
      <p className="text-medium-gray-text mb-4">Last updated: {new Date().toISOString().slice(0, 10)}</p>
      <h2 className="text-2xl font-semibold mt-8 mb-3">Overview</h2>
      <p className="text-medium-gray-text">If you are not satisfied with your purchase, you may request a refund within 7 days of the transaction for eligible items/services, subject to review.</p>
      <h2 className="text-2xl font-semibold mt-8 mb-3">Eligibility</h2>
      <ul className="list-disc ml-6 space-y-2 text-medium-gray-text">
        <li>Proof of purchase required (order ID / receipt).</li>
        <li>No refunds for already delivered digital content that has been fully accessed/consumed.</li>
        <li>Abuse or fraudulent activity voids eligibility.</li>
      </ul>
      <h2 className="text-2xl font-semibold mt-8 mb-3">Process</h2>
      <ul className="list-disc ml-6 space-y-2 text-medium-gray-text">
        <li>Contact support at info@productpulse.com with your order ID.</li>
        <li>Approved refunds are processed to the original payment method via PayFast within 7–10 business days.</li>
      </ul>
    </div>
  </div>
);

const ShippingPolicyPage = () => (
  <div className="min-h-screen bg-neutral-950 text-white pt-40 pb-12">
    <div className="max-w-4xl mx-auto px-4 py-12 text-white-text">
      <h1 className="text-3xl font-bold mb-6">Shipping and Delivery Policy</h1>
      <p className="text-medium-gray-text mb-4">Last updated: {new Date().toISOString().slice(0, 10)}</p>
      <h2 className="text-2xl font-semibold mt-8 mb-3">Digital Delivery</h2>
      <p className="text-medium-gray-text">Our products are primarily digital. Access is granted to your account after verified payment.</p>
      <h2 className="text-2xl font-semibold mt-8 mb-3">Physical Shipping (if applicable)</h2>
      <p className="text-medium-gray-text">If a physical item is offered, dispatch occurs within 3–5 business days. Delivery times vary by location.</p>
      <h2 className="text-2xl font-semibold mt-8 mb-3">Contact</h2>
      <p className="text-medium-gray-text">Questions: info@productpulse.com</p>
    </div>
  </div>
);

// Payment Policy Page for Payment Gateway Compliance
const PaymentPolicyPage = () => (
  <div className="min-h-screen bg-neutral-950 text-white pt-40 pb-12">
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
      <h1 className="text-4xl font-bold mb-4">Payment Policy</h1>
      <p className="text-gray-400 mb-8">Last updated: {new Date().toISOString().slice(0, 10)}</p>

      <div className="space-y-8">
        {/* Business Information */}
        <section className="bg-neutral-900 rounded-lg p-6 border border-white/10">
          <h2 className="text-2xl font-semibold mb-4">Business Information</h2>
          <div className="space-y-2 text-gray-300">
            <p><strong className="text-white">Business Name:</strong> ProductPulse</p>
            <p><strong className="text-white">Website:</strong> https://productpulse.com</p>
            <p><strong className="text-white">Email:</strong> info@productpulse.com</p>
            <p><strong className="text-white">Support Email:</strong> support@productpulse.com</p>
            <p><strong className="text-white">Business Type:</strong> Digital Product Marketplace / E-commerce</p>
            <p><strong className="text-white">Industry:</strong> E-commerce, Digital Products, Product Analytics</p>
          </div>
        </section>

        {/* Payment Processing */}
        <section className="bg-neutral-900 rounded-lg p-6 border border-white/10">
          <h2 className="text-2xl font-semibold mb-4">Payment Processing</h2>
          <div className="space-y-4 text-gray-300">
            <p>ProductPulse processes payments through XPay, a secure payment gateway provider. All transactions are encrypted and processed in compliance with PCI DSS standards.</p>
            <div>
              <h3 className="text-xl font-semibold mb-2 text-white">Accepted Payment Methods</h3>
              <ul className="list-disc ml-6 space-y-1">
                <li>Credit Cards (Visa, Mastercard, American Express)</li>
                <li>Debit Cards</li>
                <li>Digital Wallets (as supported by XPay)</li>
              </ul>
            </div>
            <div>
              <h3 className="text-xl font-semibold mb-2 text-white">Payment Processing</h3>
              <ul className="list-disc ml-6 space-y-1">
                <li>All payments are processed securely through XPay's payment gateway</li>
                <li>We do not store credit card information on our servers</li>
                <li>Payment data is encrypted using industry-standard SSL/TLS protocols</li>
                <li>Transactions are processed in real-time upon purchase confirmation</li>
                <li>Payment authorization occurs before product access is granted</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Security Measures */}
        <section className="bg-neutral-900 rounded-lg p-6 border border-white/10">
          <h2 className="text-2xl font-semibold mb-4">Security & Data Protection</h2>
          <div className="space-y-4 text-gray-300">
            <p>We take payment security seriously and implement multiple layers of protection:</p>
            <ul className="list-disc ml-6 space-y-2">
              <li><strong className="text-white">PCI DSS Compliance:</strong> All payment processing complies with Payment Card Industry Data Security Standards</li>
              <li><strong className="text-white">SSL Encryption:</strong> All data transmission is encrypted using 256-bit SSL/TLS encryption</li>
              <li><strong className="text-white">Tokenization:</strong> Sensitive payment information is tokenized and never stored on our servers</li>
              <li><strong className="text-white">Secure Gateway:</strong> Payments are processed through XPay's secure, PCI-compliant infrastructure</li>
              <li><strong className="text-white">Fraud Prevention:</strong> Advanced fraud detection and monitoring systems are in place</li>
              <li><strong className="text-white">Regular Audits:</strong> Security audits and compliance checks are conducted regularly</li>
            </ul>
          </div>
        </section>

        {/* Pricing & Currency */}
        <section className="bg-neutral-900 rounded-lg p-6 border border-white/10">
          <h2 className="text-2xl font-semibold mb-4">Pricing & Currency</h2>
          <div className="space-y-4 text-gray-300">
            <p><strong className="text-white">Currency:</strong> All prices are displayed in USD (United States Dollars)</p>
            <p><strong className="text-white">Price Display:</strong> Product prices are clearly displayed before purchase confirmation</p>
            <p><strong className="text-white">Taxes:</strong> Applicable taxes may be added based on your location and local regulations</p>
            <p><strong className="text-white">Price Changes:</strong> We reserve the right to modify prices, but changes will not affect completed purchases</p>
          </div>
        </section>

        {/* Refund Policy */}
        <section className="bg-neutral-900 rounded-lg p-6 border border-white/10">
          <h2 className="text-2xl font-semibold mb-4">Refund Policy</h2>
          <div className="space-y-4 text-gray-300">
            <p>Our refund policy is designed to be fair and transparent:</p>
            <div>
              <h3 className="text-xl font-semibold mb-2 text-white">Refund Eligibility</h3>
              <ul className="list-disc ml-6 space-y-1">
                <li>Refund requests must be submitted within 7 days of purchase</li>
                <li>Proof of purchase (order ID) is required</li>
                <li>Digital products that have been fully accessed may not be eligible for refund</li>
                <li>Refunds are subject to review and approval</li>
              </ul>
            </div>
            <div>
              <h3 className="text-xl font-semibold mb-2 text-white">Refund Process</h3>
              <ul className="list-disc ml-6 space-y-1">
                <li>Contact support at support@productpulse.com with your order ID</li>
                <li>Refund requests are reviewed within 2-3 business days</li>
                <li>Approved refunds are processed to the original payment method</li>
                <li>Refunds typically appear in your account within 7-10 business days</li>
              </ul>
            </div>
            <p className="text-sm text-gray-400 mt-4">For complete refund terms, please see our <a href="/refunds" className="text-indigo-400 hover:underline">Refund Policy</a>.</p>
          </div>
        </section>

        {/* Transaction Records */}
        <section className="bg-neutral-900 rounded-lg p-6 border border-white/10">
          <h2 className="text-2xl font-semibold mb-4">Transaction Records</h2>
          <div className="space-y-4 text-gray-300">
            <p>All transactions are recorded and maintained for your records:</p>
            <ul className="list-disc ml-6 space-y-1">
              <li>Transaction receipts are automatically sent to your registered email</li>
              <li>Purchase history is available in your user dashboard</li>
              <li>Transaction records are maintained for a minimum of 7 years for accounting and legal compliance</li>
              <li>You can access your transaction history at any time through your account</li>
            </ul>
          </div>
        </section>

        {/* Dispute Resolution */}
        <section className="bg-neutral-900 rounded-lg p-6 border border-white/10">
          <h2 className="text-2xl font-semibold mb-4">Dispute Resolution</h2>
          <div className="space-y-4 text-gray-300">
            <p>If you have any concerns about a transaction or payment:</p>
            <ol className="list-decimal ml-6 space-y-2">
              <li>Contact our support team at support@productpulse.com within 30 days of the transaction</li>
              <li>Provide your order ID and detailed description of the issue</li>
              <li>Our team will investigate and respond within 5 business days</li>
              <li>If unresolved, disputes may be escalated through XPay's dispute resolution process</li>
            </ol>
          </div>
        </section>

        {/* Compliance & Legal */}
        <section className="bg-neutral-900 rounded-lg p-6 border border-white/10">
          <h2 className="text-2xl font-semibold mb-4">Compliance & Legal</h2>
          <div className="space-y-4 text-gray-300">
            <ul className="list-disc ml-6 space-y-2">
              <li>We comply with all applicable financial regulations and payment processing standards</li>
              <li>All transactions are subject to our <a href="/terms" className="text-indigo-400 hover:underline">Terms of Service</a></li>
              <li>Payment processing is governed by XPay's terms and conditions</li>
              <li>We maintain compliance with PCI DSS Level 1 standards</li>
              <li>All payment data handling complies with applicable data protection regulations</li>
            </ul>
          </div>
        </section>

        {/* Contact Information */}
        <section className="bg-neutral-900 rounded-lg p-6 border border-white/10">
          <h2 className="text-2xl font-semibold mb-4">Payment Support & Contact</h2>
          <div className="space-y-4 text-gray-300">
            <div>
              <p><strong className="text-white">Payment Support Email:</strong> support@productpulse.com</p>
              <p><strong className="text-white">General Inquiries:</strong> info@productpulse.com</p>
              <p><strong className="text-white">Response Time:</strong> We aim to respond to payment-related inquiries within 24-48 hours</p>
            </div>
            <div>
              <p className="text-sm text-gray-400 mt-4">For payment gateway technical issues, please contact XPay support directly.</p>
            </div>
          </div>
        </section>

        {/* Updates */}
        <section className="bg-neutral-900 rounded-lg p-6 border border-white/10">
          <h2 className="text-2xl font-semibold mb-4">Policy Updates</h2>
          <p className="text-gray-300">We reserve the right to update this Payment Policy at any time. Changes will be posted on this page with an updated "Last updated" date. Continued use of our payment services after changes constitutes acceptance of the updated policy.</p>
        </section>
      </div>

      <div className="mt-8 text-center text-gray-400 text-sm">
        <p>For additional information, please review our <a href="/privacy" className="text-indigo-400 hover:underline">Privacy Policy</a>, <a href="/terms" className="text-indigo-400 hover:underline">Terms of Service</a>, and <a href="/refunds" className="text-indigo-400 hover:underline">Refund Policy</a>.</p>
      </div>
    </div>
  </div>
);
// Define Firestore collection names - MOVED HERE TO BE GLOBAL
const TABLES = {
  PRODUCTS: 'products',
  USERS: 'users',
  PURCHASES: 'purchases',
};

const PN_PREFIX = 'PN-';
const PN_PAD_LENGTH = 5;

const formatProductNumber = (sequence) => `${PN_PREFIX}${String(sequence).padStart(PN_PAD_LENGTH, '0')}`;

const extractProductNumberSequence = (pnValue) => {
  if (!pnValue || typeof pnValue !== 'string') return null;
  const match = pnValue.match(/PN-(\d+)/i);
  return match ? parseInt(match[1], 10) : null;
};

const assignPnNumbersIfMissing = async (products) => {
  try {
    let maxSequence = 0;
    products.forEach((product) => {
      const seq = extractProductNumberSequence(product.pnNumber);
      if (seq && seq > maxSequence) {
        maxSequence = seq;
      }
    });

    const updateOperations = [];

    products.forEach((product) => {
      if (!product.pnNumber) {
        maxSequence += 1;
        const pnNumber = formatProductNumber(maxSequence);
        product.pnNumber = pnNumber;
        if (product.id) {
          const productRef = doc(db, TABLES.PRODUCTS, product.id);
          updateOperations.push(setDoc(productRef, { pnNumber }, { merge: true }));
        }
      }
    });

    if (updateOperations.length > 0) {
      await Promise.allSettled(updateOperations);
    }
  } catch (error) {
    console.error('[PN] Failed to assign PN numbers:', error);
  }

  return products;
};

const assignPnToMockProducts = (products) =>
  products.map((product, index) => ({
    ...product,
    pnNumber: product.pnNumber || formatProductNumber(index + 1),
  }));

const getProductPnLabel = (product) => {
  if (product?.pnNumber) return product.pnNumber;
  if (product?.id) {
    const tail = product.id.toString().slice(-6).toUpperCase();
    return `${PN_PREFIX}${tail.padStart(PN_PAD_LENGTH, '0')}`;
  }
  return `${PN_PREFIX}${'0'.repeat(PN_PAD_LENGTH)}`;
};

// Helper functions fetchUserPurchases and fetchAllPurchasedProducts have been moved to APIService 

// ==================== CONTEXT & REDUCER ====================
const AppContext = createContext();

const initialState = {
  user: null,
  products: [],
  filteredProducts: [],
  globalSoldProducts: [], // Add global sold products array
  categories: [
    'Alexa Skills', 'Amazon Autos', 'Amazon Devices & Accessories', 'Amazon Explore', 'Appliances',
    'Video Games', 'Arts, Crafts & Sewing', 'Audible Books & Originals', 'Automotive', 'Baby Products',
    'Beauty & Personal Care', 'Books', 'CDs & Vinyl', 'Cell Phones & Accessories', 'Clothing, Shoes & Jewelry',
    'Collectibles & Fine Art', 'Credit & Payment Cards', 'Digital Music', 'Electronics', 'Everything Else',
    'Gift Cards', 'Grocery & Gourmet Food', 'Handmade Products', 'Health & Household', 'Home & Kitchen',
    'Industrial & Scientific', 'Kindle Store', 'Luxury Stores', 'Magazine Subscriptions', 'Movies & TV',
    'Musical Instruments', 'Office Products', 'Patio, Lawn & Garden', 'Pet Supplies', 'Prime Video',
    'Software', 'Sports & Outdoors', 'Tools & Home Improvement', 'Toys & Games', 'Video Shorts'
  ],
  filters: {
    category: '',
    search: '',
    sortBy: 'price-desc',
    priceRange: [0, 1000]
  },
  isLoading: false,
  error: null,
  darkMode: false,
  notifications: [],
  selectedProduct: null,
  currentPage: 1 // Add currentPage to global state
};

function appReducer(state, action) {
  switch (action.type) {
    case 'SET_USER':
      return { ...state, user: action.payload };
    case 'SET_PRODUCTS':
      return { ...state, products: action.payload, filteredProducts: action.payload };
    case 'SET_FILTERED_PRODUCTS':
      return { ...state, filteredProducts: action.payload };
    case 'SET_GLOBAL_SOLD_PRODUCTS':
      return { ...state, globalSoldProducts: action.payload };
    case 'SET_FILTERS':
      return { ...state, filters: { ...state.filters, ...action.payload } };
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };
    case 'SET_ERROR':
      return { ...state, error: action.payload };
    case 'TOGGLE_DARK_MODE':
      localStorage.setItem('darkMode', JSON.stringify(!state.darkMode));
      return { ...state, darkMode: !state.darkMode };
    case 'ADD_NOTIFICATION':
      return {
        ...state,
        notifications: [...state.notifications, {
          ...action.payload,
          id: uuidv4(),
          timestamp: new Date()
        }]
      };
    case 'REMOVE_NOTIFICATION':
      return {
        ...state,
        notifications: state.notifications.filter(n => n.id !== action.payload)
      };
    case 'SET_SELECTED_PRODUCT':
      return { ...state, selectedProduct: action.payload };
    case 'PURCHASE_PRODUCT':
      const updatedUser = state.user ? {
        ...state.user,
        purchasedProducts: [...state.user.purchasedProducts, action.payload]
      } : null;
      return { ...state, user: updatedUser };
    case 'SET_CURRENT_PAGE': // Add new case for currentPage
      return { ...state, currentPage: action.payload };
    default:
      return state;
  }
}

// ==================== API SERVICES ====================
class APIService {
  static cachedProducts = null;
  static cachedGlobalSoldProducts = null;

  static setCachedProducts(products) {
    this.cachedProducts = products;
  }

  static async fetchUserPurchases(userId) {
    try {
      console.log(`[APIService] Fetching purchases for user: ${userId}`);
      const purchasesRef = collection(db, TABLES.PURCHASES);
      const q = query(purchasesRef, where('user_id', '==', userId));
      const querySnapshot = await getDocs(q);

      const purchasedProductIds = [];
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        purchasedProductIds.push(data.product_id);
      });

      console.log(`[APIService] Found ${purchasedProductIds.length} purchases for user ${userId}`);
      return purchasedProductIds;
    } catch (error) {
      console.error('[APIService] Error fetching user purchases:', error);
      return [];
    }
  }

  static async fetchAllPurchasedProducts(forceRefresh = false) {
    if (!forceRefresh) {
      // 1. Check Memory Cache
      if (this.cachedGlobalSoldProducts) {
        console.log('[APIService] Returning cached global sold products');
        return this.cachedGlobalSoldProducts;
      }
      // 2. Check LocalStorage Cache
      try {
        const cachedSold = localStorage.getItem('sold_products_cache');
        if (cachedSold) {
          const { timestamp, soldIds } = JSON.parse(cachedSold);
          // Cache valid for 30 minutes
          if (Date.now() - timestamp < 30 * 60 * 1000) {
            console.log('[APIService] Returning localStorage cached sold products');
            this.cachedGlobalSoldProducts = soldIds; // Populate memory cache
            return soldIds;
          }
        }
      } catch (e) {
        console.warn('[APIService] Error reading sold cache from localStorage', e);
      }
    }

    try {
      console.log('[APIService] Fetching all purchased products globally');
      const purchasesRef = collection(db, TABLES.PURCHASES);
      const querySnapshot = await getDocs(purchasesRef);

      const purchasedProductIds = [];
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        purchasedProductIds.push(data.product_id);
      });

      this.cachedGlobalSoldProducts = purchasedProductIds;

      // Update LocalStorage
      try {
        localStorage.setItem('sold_products_cache', JSON.stringify({
          timestamp: Date.now(),
          soldIds: purchasedProductIds
        }));
      } catch (e) {
        console.warn('[APIService] Failed to cache sold products to localStorage', e);
      }

      console.log(`[APIService] Found ${purchasedProductIds.length} globally purchased products`);
      return purchasedProductIds;
    } catch (error) {
      console.error('[APIService] Error fetching all purchased products:', error);
      return [];
    }
  }

  static calculateDynamicPrice(margin, minMargin, maxMargin) {
    if (minMargin === Infinity || maxMargin === -Infinity) return 22.50;
    if (maxMargin === minMargin) return 22.50;

    // Formula: 10 + ((margin - min) / (max - min)) * (35 - 10)
    const normalized = (margin - minMargin) / (maxMargin - minMargin);
    const price = 10 + (normalized * 25);
    return parseFloat(price.toFixed(2));
  }

  static async getMarginRange() {
    // 1. Try Cache
    if (this.cachedProducts && this.cachedProducts.length > 0) {
      let min = Infinity;
      let max = -Infinity;
      this.cachedProducts.forEach(p => {
        const m = p.margin;
        if (m < min) min = m;
        if (m > max) max = m;
      });
      return { min, max };
    }

    // 2. Try Efficient Query
    try {
      // Try to fetch min/max from DB directly
      // Note: This requires an index on 'margin' field in Firestore
      const qMin = query(collection(db, TABLES.PRODUCTS), orderBy('margin', 'asc'), firestoreLimit(1));
      const qMax = query(collection(db, TABLES.PRODUCTS), orderBy('margin', 'desc'), firestoreLimit(1));

      const [snapMin, snapMax] = await Promise.all([getDocs(qMin), getDocs(qMax)]);

      let min = Infinity;
      let max = -Infinity;

      if (!snapMin.empty) {
        const d = snapMin.docs[0].data();
        min = parseFloat(d.margin || d.margin_pct || 0);
      }
      if (!snapMax.empty) {
        const d = snapMax.docs[0].data();
        max = parseFloat(d.margin || d.margin_pct || 0);
      }

      if (min === Infinity || max === -Infinity) {
        // Fallback to mock data stats if DB is empty or fails
        return { min: 18.2, max: 42.3 };
      }

      console.log(`[APIService] Margin Range (from DB query): Min=${min}, Max=${max}`);
      return { min, max };
    } catch (err) {
      console.warn('[APIService] Could not fetch margin range via query (likely missing index). Using defaults.', err);
      // Fallback defaults
      return { min: 10, max: 45 };
    }
  }

  static async fetchProducts() {
    console.log('⚡ [APIService] Loading products from local static file');

    // Check Memory Cache first
    if (this.cachedProducts) {
      return this.cachedProducts;
    }

    // Use local bundled data which is already processed headers and types
    this.setCachedProducts(localProductsData);

    // Calculate Min and Max Margins for dynamic pricing context (if needed elsewhere)
    // The local data already has dynamic prices calculated, but we might need 
    // margin stats.

    console.log(`[APIService] Loaded ${localProductsData.length} products from static cache`);
    return localProductsData;
  }

  static async fetchProductDetails(id, userId = null, userEmail = null, isUserAdmin = false) {
    console.log(`[APIService] Fetching product details for ID: ${id}, userId: ${userId}, userEmail: ${userEmail}, isUserAdmin: ${isUserAdmin}`);
    try {
      const docRef = doc(db, TABLES.PRODUCTS, id);
      const docSnap = await getDoc(docRef);

      console.log(`[APIService] Firestore query raw result for ID ${id}:`, { exists: docSnap.exists() });

      if (!docSnap.exists()) {
        console.error(`[APIService] Product not found for ID ${id}`);
        throw new Error('Product not found.');
      }

      // Get range efficiently without fetching all products
      const { min: minMargin, max: maxMargin } = await this.getMarginRange();

      const productData = docSnap.data();
      const margin = parseFloat(productData.margin || productData.margin_pct || 0);
      const dynamicPrice = APIService.calculateDynamicPrice(margin, minMargin, maxMargin);

      const product = {
        id: docSnap.id,
        ...productData,
        price: dynamicPrice, // Override price with dynamic calculation
        margin: margin
      };

      // Check if user has purchased this product
      let hasPurchased = false;
      if (userId) {
        try {
          const purchasesQuery = query(
            collection(db, TABLES.PURCHASES),
            where('user_id', '==', userId),
            where('product_id', '==', id)
          );
          const purchaseSnap = await getDocs(purchasesQuery);
          hasPurchased = !purchaseSnap.empty;
        } catch (error) {
          console.error('[APIService] Error checking purchase status:', error);
          hasPurchased = false;
        }
      }

      // Check if user is admin
      const isAdmin = isUserAdmin || (userEmail && userEmail.toLowerCase() === ADMIN_EMAIL.toLowerCase());
      console.log(`[APIService] Admin check: isAdmin=${isAdmin}, isUserAdmin=${isUserAdmin}, userEmail=${userEmail}`);

      // If user hasn't purchased AND is not admin, remove sensitive data
      if (!hasPurchased && !isAdmin) {
        const sanitizedProduct = {
          ...product,
          // Keep basic info for purchase decisions
          title: product.title,
          pnNumber: product.pnNumber,
          price: product.price, // Use the dynamic price
          margin: product.margin,
          margin_pct: product.margin,
          profit: product.profit || 0,
          roi_pct: product.roi_pct || 0,
          category: product.category || product.categories_root || 'Electronics',
          categories_root: product.category || product.categories_root || 'Electronics',
          rating: product.reviews_rating || product.rating || 0,
          reviews: product.reviews_rating_count || product.reviews || 0,
          sales_rank_90_days_avg: product.sales_rank_90_days_avg || 0,
          bought_past_month: product.bought_past_month || 0,
          buy_box_current: product.buy_box_current || 0,
          dprice: product.dprice || 0,
          image_url: product.image_url || '',
          brand_presence: product.brand_presence || 0,
          demand_vs_supply: product.demand_vs_supply || 0,
          fba_fee: product.fba_fee || 0,
          inbound_fee: product.inbound_fee || 0,
          storage_fee: product.storage_fee || 0,
          referral_fee: product.referral_fee || 0,
          weight_lb: product.weight_lb || 0,
          length_in: product.length_in || 0,
          width_in: product.width_in || 0,
          height_in: product.height_in || 0,
          buy_box_winner_count_90_days: product.buy_box_winner_count_90_days || 0,
          new_3rd_party_fba_stock: product.new_3rd_party_fba_stock || 0,
          reviews_rating: product.reviews_rating || product.rating || 0,
          reviews_rating_count: product.reviews_rating_count || product.reviews || 0,
          amazon_price: product.amazon_price || product.buy_box_current || 0,
          // Remove sensitive data
          asin: null,
          upc: null,
          ean: null,
          model: null,
          color: null,
          target_audience: null,
          is_hazmat: null,
          amazon_url: null,
          dlink: null,
          manufacturer: null,
          brand: null,
          categories_tree: null,
          buy_box_stock: null,
          buy_box_amazon_90_days_pct: null,
          buy_box_is_fba: null,
          fba_pick_pack_fee: null,
          referral_fee_pct: null,
          referral_fee_current: null,
          summary: null,
          description: null
        };

        console.log(`[APIService] Returning sanitized product (not purchased):`, sanitizedProduct);
        return sanitizedProduct;
      }

      console.log(`[APIService] Successfully fetched and returning full product (purchased):`, product);
      return product;
    } catch (err) {
      console.error(`[APIService] Exception in fetchProductDetails for ID ${id}:`, err);
      throw err;
    }
  }
}

// ==================== MOCK DATA ====================
const mockProducts = [
  {
    id: '1',
    title: 'Wireless Bluetooth Headphones Pro',
    category: 'Electronics',
    price: 79.99,
    margin: 25.5,
    summary: 'High-quality wireless headphones with active noise cancellation and premium sound',
    description: 'Immerse yourself in pure audio bliss with the Wireless Bluetooth Headphones Pro. Featuring state-of-the-art active noise cancellation, these headphones block out distractions, allowing you to focus on your music or calls. Enjoy up to 30 hours of battery life on a single charge and experience crystal-clear highs and deep, rich bass thanks to their precision-engineered drivers. The ergonomic design ensures a comfortable fit for extended listening sessions, making them perfect for commuters, travelers, and audio enthusiasts alike. Comes with a sleek carrying case and quick-charge support.',
    criteria: ['Active Noise Cancellation', 'Long Battery Life', 'Comfortable Fit', 'High Sound Quality'],
    features: ['Bluetooth 5.0', 'Quick Charge (10 min for 5 hrs)', 'Voice Assistant Integration', 'Foldable Design', 'Multi-device Pairing'],
    distributorLinks: {
      us: 'https://distributor.com/product1',
      amazon: 'https://amazon.com/product1'
    },
    fbaFees: 8.50,
    shippingFees: 3.99,
    rating: 4.8,
    reviews: 2847,
    imageUrl: 'https://via.placeholder.com/400x300/FF3366/000000?text=Headphones',
    lastUpdated: '2024-05-30T10:30:00Z'
  },
  {
    id: '2',
    title: 'Smart Fitness Tracker Elite',
    category: 'Electronics',
    price: 49.99,
    margin: 18.2,
    summary: 'Advanced fitness tracking with heart rate monitoring and comprehensive health insights',
    description: 'Take control of your health journey with the Smart Fitness Tracker Elite. This advanced wearable offers continuous heart rate monitoring, precise sleep tracking, and built-in GPS for accurate activity mapping. Receive smart notifications directly on your wrist, allowing you to stay connected without pulling out your phone. Its water-resistant design (IP68) makes it suitable for swimming and showering, while the vibrant AMOLED display provides clear visibility in any light. With a 7-day battery life, it keeps up with your active lifestyle.',
    criteria: ['Heart Rate Monitor', 'GPS Tracking', 'Water Resistant', 'Long Battery Life'],
    features: ['24/7 Health Monitoring', 'Water Resistant IP68', '7-Day Battery', 'Smart Notifications', 'Customizable Watch Faces', 'Stress Tracking'],
    distributorLinks: {
      us: 'https://distributor.com/product2',
      amazon: 'https://amazon.com/product2'
    },
    fbaFees: 6.25,
    shippingFees: 2.99,
    rating: 4.6,
    reviews: 1923,
    imageUrl: 'https://via.placeholder.com/400x300/FF3366/000000?text=Fitness+Tracker',
    lastUpdated: '2024-05-30T09:15:00Z'
  },
  {
    id: '3',
    title: 'Portable Espresso Maker Deluxe',
    category: 'Home & Kitchen',
    price: 34.99,
    margin: 22.8,
    summary: 'Compact espresso maker for perfect coffee anywhere - home, office, or travel',
    description: 'Never be without your favorite espresso shot again with the Portable Espresso Maker Deluxe. This ingenious manual espresso maker requires no electricity, making it ideal for camping, hiking, or small kitchens. Simply add hot water and your favorite coffee grounds, pump, and enjoy a rich, creamy espresso with a perfect crema. Its durable, food-grade materials and easy-to-disassemble design ensure hassle-free cleaning. A must-have for coffee aficionados on the go.',
    criteria: ['Portable Design', 'No Electricity Required', 'Easy to Clean', 'Durable Construction'],
    features: ['Manual Operation', 'Premium Food-Grade Materials', 'Compact Size (Fits in Cup Holder)', 'Easy Maintenance', 'Built-in Espresso Cup'],
    distributorLinks: {
      us: 'https://distributor.com/product3',
      amazon: 'https://amazon.com/product3'
    },
    fbaFees: 4.75,
    shippingFees: 3.50,
    rating: 4.4,
    reviews: 756,
    imageUrl: 'https://via.placeholder.com/400x300/FF3366/000000?text=Espresso+Maker',
    lastUpdated: '2024-05-30T08:45:00Z'
  },
  {
    id: '4',
    title: 'Premium Yoga Mat with Alignment',
    category: 'Sports & Outdoors',
    price: 24.99,
    margin: 35.1,
    summary: 'Non-slip premium yoga mat with alignment lines for perfect practice',
    description: 'Elevate your yoga practice with our Premium Yoga Mat, engineered for superior grip and stability. Made from eco-friendly TPE material, this mat is free from latex, PVC, and heavy metals, ensuring a healthy and safe practice. The innovative alignment lines help you maintain correct posture and balance, deepening your stretches and enhancing your flow. Lightweight and durable, it comes with a convenient carry strap for easy transport to the studio or park. Sweat-resistant and easy to clean, it\'s designed for sustained performance.',
    criteria: ['Non-Slip Surface', 'Eco-Friendly', 'Alignment Lines', 'Long Battery Life'],
    features: ['6mm Thickness', 'TPE Material', 'Carry Strap Included', 'Sweat Resistant', 'Double-Sided Grip', 'Odourless'],
    distributorLinks: {
      us: 'https://distributor.com/product4',
      amazon: 'https://amazon.com/product4'
    },
    fbaFees: 3.25,
    shippingFees: 4.99,
    rating: 4.7,
    reviews: 1245,
    imageUrl: 'https://via.placeholder.com/400x300/FF3366/000000?text=Yoga+Mat',
    lastUpdated: '2024-05-30T07:20:00Z'
  },
  {
    id: '5',
    title: 'Smart LED Desk Lamp Pro',
    category: 'Home & Kitchen',
    price: 39.99,
    margin: 28.7,
    summary: 'Adjustable LED desk lamp with wireless charging and smart controls',
    description: 'Illuminate your workspace with the Smart LED Desk Lamp Pro, a versatile lighting solution for modern productivity. This lamp offers multiple brightness levels and color temperatures (from warm white to cool daylight), adjustable via intuitive touch controls. Its built-in Qi-compatible wireless charging pad keeps your smartphone powered up and within reach. With a flexible gooseneck and memory function, it remembers your last setting for convenience. Designed to be eye-friendly, it minimizes flicker and glare, reducing eye strain during long hours of work or study.',
    criteria: ['Adjustable Brightness', 'Wireless Charging', 'Touch Control', 'Energy Efficient'],
    features: ['USB-C Fast Charging', 'Eye Protection Technology', 'Memory Function', 'Touch Sensitive Controls', 'Flexible Gooseneck', 'Multiple Color Temperatures'],
    distributorLinks: {
      us: 'https://distributor.com/product5',
      amazon: 'https://amazon.com/product5'
    },
    fbaFees: 5.50,
    shippingFees: 3.25,
    rating: 4.5,
    reviews: 892,
    imageUrl: 'https://via.placeholder.com/400x300/FF3366/000000?text=Desk+Lamp',
    lastUpdated: '2024-05-30T11:10:00Z'
  },
  {
    id: '6',
    title: 'Ultra-Fast Wireless Charger',
    category: 'Electronics',
    price: 19.99,
    margin: 42.3,
    summary: 'Fast wireless charging pad with advanced safety features',
    description: 'Experience lightning-fast charging with the Ultra-Fast Wireless Charger. This sleek and compact charging pad supports Qi-compatible devices, delivering up to 15W of power for rapid charging. Equipped with advanced safety features such as over-temperature protection, over-voltage protection, and foreign object detection, it ensures a safe and efficient charge every time. The discreet LED indicator confirms charging status without being overly distracting. Its case-friendly design means you don\'t need to remove your phone cover for charging.',
    criteria: ['Fast Charging', 'Universal Compatibility', 'LED Indicator', 'Safety Features'],
    features: ['15W Fast Charging', 'Over-heat Protection', 'Foreign Object Detection', 'Case Friendly (up to 5mm)', 'Non-slip Surface', 'Slim Design'],
    distributorLinks: {
      us: 'https://distributor.com/product6',
      amazon: 'https://amazon.com/product6'
    },
    fbaFees: 2.85,
    shippingFees: 2.50,
    rating: 4.3,
    reviews: 634,
    imageUrl: 'https://via.placeholder.com/400x300/FF3366/000000?text=Wireless+Charger',
    lastUpdated: '2024-05-30T06:55:00Z'
  },
  {
    id: '7',
    title: 'Premium Gaming Mouse RGB',
    category: 'Video Games',
    price: 89.99,
    margin: 30.2,
    summary: 'High-performance gaming mouse with customizable RGB lighting and precision sensors',
    description: 'Dominate your gaming sessions with the Premium Gaming Mouse RGB. Featuring a 25,600 DPI optical sensor for pixel-perfect accuracy, customizable RGB lighting with 16.8 million colors, and programmable buttons for ultimate control. The ergonomic design ensures comfort during extended gaming sessions, while the premium switches provide tactile feedback and durability. Compatible with all major gaming platforms and includes software for advanced customization.',
    criteria: ['High DPI Sensor', 'RGB Lighting', 'Programmable Buttons', 'Ergonomic Design'],
    features: ['25,600 DPI Optical Sensor', 'Customizable RGB', '8 Programmable Buttons', 'Premium Switches', 'Ergonomic Design', 'Software Included'],
    distributorLinks: {
      us: 'https://distributor.com/product7',
      amazon: 'https://amazon.com/product7'
    },
    fbaFees: 7.25,
    shippingFees: 3.99,
    rating: 4.9,
    reviews: 2156,
    imageUrl: 'https://via.placeholder.com/400x300/FF3366/000000?text=Gaming+Mouse',
    lastUpdated: '2024-05-30T12:30:00Z'
  },
  {
    id: '8',
    title: 'Organic Baby Food Variety Pack',
    category: 'Baby Products',
    price: 29.99,
    margin: 40.1,
    summary: 'Nutritious organic baby food with natural ingredients and no artificial preservatives',
    description: 'Give your little one the best start with our Organic Baby Food Variety Pack. Made with 100% organic fruits and vegetables, this pack includes 24 pouches of delicious, nutritious food perfect for babies 6+ months. Each pouch is free from artificial preservatives, colors, and flavors, ensuring your baby gets only the purest ingredients. The convenient pouches are easy to use on-the-go and feature a resealable cap for freshness.',
    criteria: ['Organic Ingredients', 'No Artificial Preservatives', 'Convenient Packaging', 'Age Appropriate'],
    features: ['100% Organic', '24 Pouches', 'Resealable Caps', '6+ Months', 'No Artificial Additives', 'Easy to Use'],
    distributorLinks: {
      us: 'https://distributor.com/product8',
      amazon: 'https://amazon.com/product8'
    },
    fbaFees: 4.50,
    shippingFees: 2.99,
    rating: 4.7,
    reviews: 892,
    imageUrl: 'https://via.placeholder.com/400x300/FF3366/000000?text=Baby+Food',
    lastUpdated: '2024-05-30T13:15:00Z'
  },
  {
    id: '9',
    title: 'Professional Makeup Brush Set',
    category: 'Beauty & Personal Care',
    price: 44.99,
    margin: 35.8,
    summary: 'Complete professional makeup brush set with synthetic bristles and ergonomic handles',
    description: 'Achieve flawless makeup application with our Professional Makeup Brush Set. This comprehensive 15-piece set includes all the essential brushes for a complete makeup look, from foundation to eyeshadow to contouring. Each brush features high-quality synthetic bristles that are soft, durable, and cruelty-free. The ergonomic handles provide perfect control and comfort during application. Perfect for both beginners and professionals.',
    criteria: ['Professional Quality', 'Synthetic Bristles', 'Complete Set', 'Ergonomic Design'],
    features: ['15-Piece Set', 'Synthetic Bristles', 'Cruelty-Free', 'Ergonomic Handles', 'Professional Quality', 'Complete Coverage'],
    distributorLinks: {
      us: 'https://distributor.com/product9',
      amazon: 'https://amazon.com/product9'
    },
    fbaFees: 5.75,
    shippingFees: 3.50,
    rating: 4.6,
    reviews: 1247,
    imageUrl: 'https://via.placeholder.com/400x300/FF3366/000000?text=Makeup+Brushes',
    lastUpdated: '2024-05-30T14:20:00Z'
  }
];

// ==================== UTILITY HOOKS ====================
const useAuth = () => {
  const { state, dispatch } = useContext(AppContext);

  // Helper to check if username is unique in Firestore
  const isUsernameUnique = async (username) => {
    console.log('[Auth] Checking username uniqueness (Firebase): ', username);
    try {
      if (!username || username.trim() === '') {
        console.log('[Auth] Empty username provided');
        return false;
      }

      const usersRef = collection(db, TABLES.USERS);
      const q = query(usersRef, where('username', '==', username.trim()));
      const querySnapshot = await getDocs(q);
      const isUnique = querySnapshot.empty;
      console.log('[Auth] Username uniqueness Firebase result:', { isUnique, username: username.trim(), docsFound: querySnapshot.size });
      return isUnique;
    } catch (e) {
      console.error('[Auth] Exception during username uniqueness check (Firebase):', e, 'Message:', e.message);
      // Return true to allow registration if check fails (network issues, etc.)
      console.log('[Auth] Returning true due to check failure to avoid blocking registration');
      return true;
    }
  };

  const login = async (email, password) => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      console.log('[Auth] Attempting login with Firebase Auth:', { email, password });
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const userAuth = userCredential.user;
      console.log('[Auth] Login response from Firebase Auth:', { userAuth });

      if (!userAuth) {
        throw new Error('Login failed: No user object received.');
      }

      // Fetch user details from Firestore custom users collection
      console.log('[Auth] Fetching custom user data from Firestore...');
      const userDocRef = doc(db, TABLES.USERS, userAuth.uid);
      const userDocSnap = await getDoc(userDocRef);
      let customUser = null;
      if (userDocSnap.exists()) {
        customUser = userDocSnap.data();
        console.log('[Auth] Custom user data found:', customUser);
      } else {
        console.warn('[Auth] Custom user data not found in Firestore for UID:', userAuth.uid, '- Attempting to create a basic record.');
        // Create a basic user record if not found (e.g., for users signed up via other methods)
        customUser = {
          id: userAuth.uid,
          email: userAuth.email,
          first_name: '',
          last_name: '',
          username: userAuth.email.split('@')[0], // Basic username from email
          is_admin: false,
        };
        await setDoc(userDocRef, customUser, { merge: true });
        console.log('[Auth] Basic user record created in Firestore.');
      }

      // Fetch purchased products from Firestore
      const purchasedProductIds = await APIService.fetchUserPurchases(userAuth.uid);
      console.log('[Auth] Fetched purchased product IDs:', purchasedProductIds);

      const user = {
        id: userAuth.uid,
        email: userAuth.email,
        username: customUser?.username || userAuth.email.split('@')[0], // Add username field
        isAdmin: customUser?.is_admin || false,
        purchasedProducts: purchasedProductIds,
        tokenUsage: customUser?.token_usage || 0, // Assuming these will be stored in Firestore customUser doc
        maxTokens: customUser?.max_tokens || 100
      };
      dispatch({ type: 'SET_USER', payload: user });
      dispatch({ type: 'ADD_NOTIFICATION', payload: { type: 'success', message: 'Successfully logged in!' } });
      return user;
    } catch (error) {
      let msg = error.message || 'Login failed.';
      if (error.code === 'auth/invalid-credential' || error.code === 'auth/user-not-found') {
        msg = 'Incorrect email or password.';
      } else if (error.code === 'auth/too-many-requests') {
        msg = 'Too many login attempts. Please try again later.';
      }
      console.error('[Auth] Login error (caught Firebase):', error, 'Message:', msg);
      dispatch({ type: 'SET_ERROR', payload: msg });
      dispatch({ type: 'ADD_NOTIFICATION', payload: { type: 'error', message: msg } });
      throw new Error(msg);
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  const register = async (email, password, firstName, lastName, username) => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      console.log('[Auth] Attempting registration with Firebase Auth:', { email, password, firstName, lastName, username });

      // Check username uniqueness in Firestore
      const unique = await isUsernameUnique(username);
      console.log('[Auth] Username unique check result:', unique);
      if (!unique) {
        const msg = 'Username is already taken. Please choose another.';
        console.warn('[Auth] Registration failed: Username not unique.');
        dispatch({ type: 'SET_ERROR', payload: msg });
        dispatch({ type: 'ADD_NOTIFICATION', payload: { type: 'error', message: msg } });
        throw new Error(msg);
      }

      // Create user with Firebase Auth
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const userAuth = userCredential.user;
      console.log('[Auth] Firebase Auth signUp successful:', { userAuth });

      if (!userAuth) {
        throw new Error('Registration failed: No user object received.');
      }

      // Create custom user record in Firestore
      console.log('[Auth] Creating custom user record in Firestore...');
      const userDocRef = doc(db, TABLES.USERS, userAuth.uid);
      const customUser = {
        id: userAuth.uid,
        email: userAuth.email,
        first_name: firstName,
        last_name: lastName,
        username: username,
        is_admin: false, // Default to false for new registrations
        token_usage: 0,
        max_tokens: 100
      };
      await setDoc(userDocRef, customUser);
      console.log('[Auth] Custom user record created/updated in Firestore.');

      // For new users, purchased products will be empty - don't fetch from Firestore unnecessarily
      const purchasedProductIds = [];
      console.log('[Auth] New user - no purchased products initially');

      // Set user state in context (same as login function)
      const user = {
        id: userAuth.uid,
        email: userAuth.email,
        username: customUser.username,
        isAdmin: customUser.is_admin || false,
        purchasedProducts: purchasedProductIds,
        tokenUsage: customUser.token_usage || 0,
        maxTokens: customUser.max_tokens || 100
      };
      dispatch({ type: 'SET_USER', payload: user });

      // Send email verification
      // Note: For email verification to work, you need to enable it in Firebase Console
      // and potentially configure email templates.
      // await sendEmailVerification(userAuth);
      // console.log('[Auth] Email verification sent to:', userAuth.email);

      dispatch({ type: 'ADD_NOTIFICATION', payload: { type: 'success', message: 'Registration successful! Welcome to ProductPulse!' } });
      return user;
    } catch (error) {
      let msg = error.message || 'Registration failed.';

      // Handle specific Firebase Auth errors
      if (error.code === 'auth/email-already-in-use') {
        msg = 'Email is already registered. Please use another email or log in.';
      } else if (error.code === 'auth/weak-password') {
        msg = 'Password is too weak. Please choose a stronger password.';
      } else if (error.code === 'auth/invalid-email') {
        msg = 'Please enter a valid email address.';
      } else if (error.code === 'auth/operation-not-allowed') {
        msg = 'Email/password accounts are not enabled. Please contact support.';
      } else if (error.code === 'auth/network-request-failed') {
        msg = 'Network error. Please check your internet connection and try again.';
      } else if (error.code === 'permission-denied' || error.message?.includes('PERMISSION_DENIED')) {
        msg = 'Unable to create account. Please contact support.';
      } else if (error.message?.includes('firestore')) {
        msg = 'Database error. Please try again in a moment.';
      }

      console.error('[Auth] Register error (caught Firebase):', error, 'Message:', msg, 'Code:', error.code);
      dispatch({ type: 'SET_ERROR', payload: msg });
      dispatch({ type: 'ADD_NOTIFICATION', payload: { type: 'error', message: msg } });
      throw new Error(msg);
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  const logout = async () => {
    try {
      await signOut(auth);
      dispatch({ type: 'SET_USER', payload: null });
      dispatch({ type: 'ADD_NOTIFICATION', payload: { type: 'info', message: 'Logged out successfully!' } });
    } catch (error) {
      console.error('[Auth] Logout error (Firebase):', error);
      dispatch({ type: 'ADD_NOTIFICATION', payload: { type: 'error', message: 'Error logging out.' } });
    }
  };

  return { user: state.user, login, register, logout };
};

const useProducts = () => {
  const { state, dispatch } = useContext(AppContext);

  const fetchProducts = useCallback(async () => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });

      // Fetch products and sold products in parallel for better performance
      const [products, globalSoldProductIds] = await Promise.all([
        APIService.fetchProducts(),
        APIService.fetchAllPurchasedProducts()
      ]);

      dispatch({ type: 'SET_PRODUCTS', payload: products });
      dispatch({
        type: 'SET_GLOBAL_SOLD_PRODUCTS',
        payload: globalSoldProductIds,
      });
    } catch (error) {
      console.error('Error fetching products:', error);
      dispatch({ type: 'SET_ERROR', payload: error.message });
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  }, [dispatch]);

  const filterProducts = (products, filters) => {
    let filtered = [...products];

    if (filters.category) {
      filtered = filtered.filter(p => {
        const productCategory = (p.category || p.categories_root || '').toLowerCase();
        return productCategory === filters.category.toLowerCase();
      });
    }

    if (filters.search) {
      const searchTerm = filters.search.toLowerCase();
      filtered = filtered.filter(p =>
        p.title.toLowerCase().includes(searchTerm) ||
        p.summary.toLowerCase().includes(searchTerm) ||
        (p.asin && p.asin.toLowerCase().includes(searchTerm)) ||
        (p.pnNumber && p.pnNumber.toLowerCase().includes(searchTerm))
      );
    }

    if (filters.priceRange && filters.priceRange.length === 2) {
      filtered = filtered.filter(p =>
        p.price >= filters.priceRange[0] && p.price <= filters.priceRange[1]
      );
    }

    // Apply sorting
    switch (filters.sortBy) {
      case 'price':
        filtered.sort((a, b) => (a.price || a.dprice || 0) - (b.price || b.dprice || 0));
        break;
      case 'price-desc':
        filtered.sort((a, b) => (b.price || b.dprice || 0) - (a.price || a.dprice || 0));
        break;
      case 'margin':
        filtered.sort((a, b) => (b.margin || b.margin_pct || 0) - (a.margin || a.margin_pct || 0));
        break;
      case 'profit':
        filtered.sort((a, b) => (b.profit || 0) - (a.profit || 0));
        break;
      case 'roi':
        filtered.sort((a, b) => (b.roi_pct || 0) - (a.roi_pct || 0));
        break;
      case 'rating':
        filtered.sort((a, b) => b.rating - a.rating);
        break;
      default:
        break;
    }

    return filtered;
  };

  const updateFilters = (newFilters) => {
    dispatch({ type: 'SET_FILTERS', payload: newFilters });
  };

  const purchaseProduct = async (productId) => {
    if (!state.user) {
      dispatch({ type: 'ADD_NOTIFICATION', payload: { type: 'error', message: 'Please log in to purchase products.' } });
      return;
    }
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      // Get current auth user from Firebase Auth
      const authUser = auth.currentUser;
      const userIdToUse = authUser?.uid || state.user.id;

      if (!userIdToUse) {
        throw new Error('User ID not available for purchase.');
      }

      // Insert into Firestore purchases collection
      const newPurchaseRef = await addDoc(collection(db, TABLES.PURCHASES), {
        user_id: userIdToUse,
        product_id: productId,
        purchased_at: new Date().toISOString(),
      });
      console.log('Purchase added to Firestore with ID:', newPurchaseRef.id);

      // Fetch all purchases for the user after purchase to update state
      const purchasedProductIds = await APIService.fetchUserPurchases(userIdToUse);
      dispatch({
        type: 'SET_USER',
        payload: {
          ...state.user,
          purchasedProducts: purchasedProductIds,
        },
      });

      // Fetch and update global sold products (force refresh)
      const globalSoldProductIds = await APIService.fetchAllPurchasedProducts(true);
      dispatch({
        type: 'SET_GLOBAL_SOLD_PRODUCTS',
        payload: globalSoldProductIds,
      });

      // === Send product details email to user ===
      try {
        // Fetch full product details (with all fields)
        const product = await APIService.fetchProductDetails(productId, userIdToUse);
        // Prepare template params
        const templateParams = {
          to_email: state.user.email,
          user_name: state.user.username || state.user.email,
          product_title: product.title || '',
          product_category: product.category || product.categories_root || '',
          product_price: product.price || product.dprice || 0,
          product_margin: product.margin || product.margin_pct || 0,
          product_profit: product.profit || 0,
          product_roi: product.roi_pct || 0,
          product_reviews: product.reviews_rating_count || product.reviews || 0,
          product_rating: product.reviews_rating || product.rating || 0,
          product_sales_rank: product.sales_rank_90_days_avg || '',
          product_bought_past_month: product.bought_past_month || '',
          product_ip_complaint: 'None',
          product_amazon_presence: product.buy_box_amazon_90_days_pct || '',
          product_amazon_price: product.amazon_price || product.buy_box_current || 0,
          product_distributor_price: product.dprice || 0,
          product_variation: 'No',
          product_weight: product.weight_lb || product.weight || '',
          product_length: product.length_in || '',
          product_width: product.width_in || '',
          product_height: product.height_in || '',
          product_asin: Array.isArray(product.asin) ? product.asin[0] : (product.asin || ''),
          product_upc: Array.isArray(product.upc) ? product.upc[0] : (product.upc || ''),
          product_ean: Array.isArray(product.ean) ? product.ean[0] : (product.ean || ''),
          product_model: product.model || '',
          product_color: product.color || '',
          product_target_audience: product.target_audience || '',
          product_hazmat: product.is_hazmat !== undefined ? (product.is_hazmat ? 'Yes' : 'No') : '',
          amazon_link: product.amazon_url || '',
          distributor_link: product.dlink || '',
          email: state.user.email,
        };
        // Send email using new emailjs config
        const serviceID = process.env.REACT_APP_EMAILJS_SERVICE_ID2;
        const templateID = process.env.REACT_APP_EMAILJS_TEMPLATE_ID3;
        const publicKey = process.env.REACT_APP_EMAILJS_PUBLIC_KEY2;
        if (serviceID && templateID && publicKey) {
          await emailjs.send(serviceID, templateID, templateParams, publicKey);
          console.log('[purchaseProduct] Product details email sent to user:', state.user.email);
        } else {
          console.warn('[purchaseProduct] EmailJS env vars for purchase email are missing.');
        }
      } catch (emailError) {
        console.error('[purchaseProduct] Error sending product details email:', emailError);
        // Do not block purchase flow if email fails
      }
      // === End email logic ===

      dispatch({ type: 'ADD_NOTIFICATION', payload: { type: 'success', message: 'Product purchased successfully!' } });
    } catch (error) {
      console.error('Purchase error (Firebase):', error);
      dispatch({ type: 'SET_ERROR', payload: error.message });
      dispatch({ type: 'ADD_NOTIFICATION', payload: { type: 'error', message: `Failed to purchase product: ${error.message}` } });
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  const selectProduct = (product) => {
    dispatch({ type: 'SET_SELECTED_PRODUCT', payload: product });
  };

  // Re-apply filters whenever products or filters change
  useEffect(() => {
    if (state.products.length > 0) {
      const filtered = filterProducts(state.products, state.filters);
      dispatch({ type: 'SET_FILTERED_PRODUCTS', payload: filtered });
    }
  }, [state.products, state.filters, dispatch]);

  return {
    products: state.filteredProducts,
    allProducts: state.products,
    globalSoldProducts: state.globalSoldProducts, // Expose global sold products
    filters: state.filters,
    categories: state.categories,
    selectedProduct: state.selectedProduct,
    currentPage: state.currentPage, // Expose currentPage from global state
    setCurrentPage: (page) => dispatch({ type: 'SET_CURRENT_PAGE', payload: page }), // Expose setter for currentPage
    fetchProducts,
    updateFilters,
    purchaseProduct,
    selectProduct
  };
};

// ==================== COMPONENTS ====================

// Notification Component
const Notification = ({ notification, onRemove }) => {
  useEffect(() => {
    const timer = setTimeout(() => {
      onRemove(notification.id);
    }, 5000);

    return () => clearTimeout(timer);
  }, [notification.id, onRemove]);

  const getIcon = () => {
    switch (notification.type) {
      case 'success':
        return <CheckCircle className="h-5 w-5 text-emerald-500" />;
      case 'error':
        return <Shield className="h-5 w-5 text-rose-500" />;
      case 'info':
        return <Info className="h-5 w-5 text-sky-500" />;
      default:
        return <Bell className="h-5 w-5 text-light-gray-text" />;
    }
  };

  const getBgColor = () => {
    switch (notification.type) {
      case 'success':
        return 'bg-emerald-900/40 border-emerald-700/50';
      case 'error':
        return 'bg-neutral-900/40 border-rose-700/50';
      case 'info':
        return 'bg-sky-900/40 border-sky-700/50';
      default:
        return 'bg-dark-gray/40 border-medium-gray-text/50';
    }
  };

  return (
    <div className={`fixed top-20 right-4 z-50 max-w-sm w-full p-4 border rounded-lg shadow-lg ${getBgColor()} animate-slide-in-right backdrop-blur-sm`}>
      <div className="flex items-start">
        <div className="flex-shrink-0">
          {getIcon()}
        </div>
        <div className="ml-3 flex-1">
          <p className="text-sm font-medium text-white-text">
            {notification.message}
          </p>
        </div>
        <button
          onClick={() => onRemove(notification.id)}
          className="ml-4 text-medium-gray-text hover:text-white-text transition-colors duration-200"
          aria-label="Close notification"
        >
          <span className="sr-only">Close</span>
          <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
          </svg>
        </button>
      </div>
    </div>
  );
};

// Hero Section Component

const HeroSection = () => {

  return (
    <div id="hero-section" className="relative min-h-screen overflow-hidden flex items-center text-white bg-neutral-950">
      {/* Clean gradient background */}
      {/* Clean gradient background with Aurora effect */}
      <div className="absolute inset-0 bg-neutral-950">
        <div className="absolute inset-0 animate-aurora opacity-30"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-neutral-950/50 to-neutral-950"></div>
      </div>

      {/* Simple grid */}
      <div className="absolute inset-0 opacity-[0.15]" style={{
        backgroundImage: `url("data:image/svg+xml,%3Csvg width='80' height='80' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M 80 0 L 0 0 0 80' fill='none' stroke='rgba(99,102,241,0.3)' stroke-width='1'/%3E%3C/svg%3E")`,
        backgroundSize: '80px 80px',
        backgroundAttachment: 'fixed'
      }}></div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32 w-full">
        <div className="text-center">
          {/* Main Content - Static, no carousel */}
          <div className="mb-8 md:mb-12">
            <h1 className="text-4xl sm:text-6xl md:text-8xl font-black mb-6 leading-tight tracking-tight">
              <span className="text-white">Discover</span>
              <br />
              <span className="bg-gradient-to-r from-blue-400 via-indigo-500 to-violet-500 bg-clip-text text-transparent">
                Premium Products
              </span>
            </h1>
            <p className="text-xl md:text-2xl text-gray-400 mb-12 max-w-3xl mx-auto font-light">
              Unlock profitable opportunities with data-driven insights
            </p>
          </div>

          {/* CTA Buttons - Simple */}
          <div className="flex flex-col sm:flex-row gap-6 justify-center mt-8">
            <Link
              to="/products"
              className="px-10 py-5 bg-gradient-to-r from-indigo-800/85 to-blue-800/85 backdrop-blur-md border border-indigo-500/20 text-white rounded-xl font-bold text-lg transition-all duration-300 hover:from-indigo-700/95 hover:to-blue-700/95 hover:border-indigo-400/40 shadow-xl shadow-indigo-900/30 hover:shadow-indigo-500/50 transform hover:scale-105"
            >
              <span className="flex items-center justify-center">
                Explore Products
                <ArrowRight className="inline ml-2 h-5 w-5" />
              </span>
            </Link>
            <Link
              to="/about"
              className="px-10 py-5 border-2 border-white/20 text-white rounded-xl font-bold text-lg transition-all duration-200 hover:bg-white/10 hover:border-white/40"
            >
              Learn More
            </Link>
          </div>
        </div>

        {/* Simple Feature cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-24 relative z-10">
          {/* Card 1: Lightning Fast */}
          <div className="group relative p-8 rounded-3xl bg-neutral-900/40 border border-white/5 backdrop-blur-sm hover:bg-neutral-900/60 transition-all duration-500 hover:-translate-y-2 hover:border-indigo-500/30 overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-blue-600/10 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>

            <div className="relative z-10">
              <div className="w-14 h-14 mb-6 rounded-2xl bg-blue-500/10 flex items-center justify-center border border-blue-500/20 group-hover:scale-110 transition-transform duration-500 shadow-[0_0_30px_-5px_rgba(59,130,246,0.3)]">
                <Zap className="h-7 w-7 text-blue-400" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-3 group-hover:text-blue-400 transition-colors">Real-Time Data</h3>
              <p className="text-gray-400 leading-relaxed text-sm">
                Stop guessing. Get live market analytics and lightning-fast insights that give you the unfair advantage.
              </p>
            </div>

            {/* Decor */}
            <div className="absolute -right-4 -bottom-4 w-24 h-24 bg-blue-500/20 blur-3xl rounded-full group-hover:bg-blue-500/30 transition-colors"></div>
          </div>

          {/* Card 2: Security */}
          <div className="group relative p-8 rounded-3xl bg-neutral-900/40 border border-white/5 backdrop-blur-sm hover:bg-neutral-900/60 transition-all duration-500 hover:-translate-y-2 hover:border-indigo-500/30 overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-indigo-600/10 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>

            <div className="relative z-10">
              <div className="w-14 h-14 mb-6 rounded-2xl bg-indigo-500/10 flex items-center justify-center border border-indigo-500/20 group-hover:scale-110 transition-transform duration-500 shadow-[0_0_30px_-5px_rgba(99,102,241,0.3)]">
                <Shield className="h-7 w-7 text-indigo-400" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-3 group-hover:text-indigo-400 transition-colors">Trusted by Many</h3>
              <p className="text-gray-400 leading-relaxed text-sm">
                Join a winning community. With over 100+ exclusive product insights sold, we are the proven choice for serious sellers.
              </p>
            </div>

            {/* Decor */}
            <div className="absolute -right-4 -bottom-4 w-24 h-24 bg-indigo-500/20 blur-3xl rounded-full group-hover:bg-indigo-500/30 transition-colors"></div>
          </div>

          {/* Card 3: Quality */}
          <div className="group relative p-8 rounded-3xl bg-neutral-900/40 border border-white/5 backdrop-blur-sm hover:bg-neutral-900/60 transition-all duration-500 hover:-translate-y-2 hover:border-purple-500/30 overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-purple-600/10 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>

            <div className="relative z-10">
              <div className="w-14 h-14 mb-6 rounded-2xl bg-purple-500/10 flex items-center justify-center border border-purple-500/20 group-hover:scale-110 transition-transform duration-500 shadow-[0_0_30px_-5px_rgba(168,85,247,0.3)]">
                <Award className="h-7 w-7 text-purple-400" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-3 group-hover:text-purple-400 transition-colors">Verified Profit</h3>
              <p className="text-gray-400 leading-relaxed text-sm">
                Every product is hand-vetted for maximum ROI. We only show you winners that are ready to scale immediately.
              </p>
            </div>

            {/* Decor */}
            <div className="absolute -right-4 -bottom-4 w-24 h-24 bg-purple-500/20 blur-3xl rounded-full group-hover:bg-purple-500/30 transition-colors"></div>
          </div>
        </div>
      </div>
    </div>
  );
};


// Navigation Bar Component
const NavBar = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/');
    setIsMobileMenuOpen(false);
  };

  const MobileLink = ({ to, icon: Icon, text }) => (
    <Link
      to={to}
      onClick={() => setIsMobileMenuOpen(false)}
      className="flex items-center space-x-3 px-4 py-3 rounded-xl text-gray-300 hover:text-white hover:bg-white/10 transition-all duration-200"
    >
      <Icon className="h-5 w-5 text-indigo-400" />
      <span className="font-medium">{text}</span>
    </Link>
  );

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 backdrop-blur-2xl bg-neutral-950/60 border-b border-white/10 shadow-2xl">
      <div className="w-full px-4 sm:px-8 lg:px-12">
        <div className="flex justify-between items-center h-20">
          <div className="flex items-center">
            <Link to="/" className="flex items-center justify-center space-x-1 group ml-0 translate-y-2">
              {/* Logo with depth effect - shadow on top left */}
              <div className="relative group-hover:scale-105 transition-all duration-300 flex items-center justify-center">
                {/* Top-left shadow for depth */}
                <div className="absolute -top-0.5 -left-0.5 w-full h-full opacity-40 blur-sm">
                  <img
                    src="/FI.png"
                    alt="Shadow"
                    className="h-56 w-auto object-contain brightness-0"
                  />
                </div>
                {/* Main logo */}
                <img
                  src="/FI.png"
                  alt="AMZProdHunter"
                  className="relative z-10 h-56 w-auto object-contain drop-shadow-[0_4px_8px_rgba(0,0,0,0.3)]"
                />
              </div>
            </Link>
          </div>

          <div className="hidden md:flex items-center space-x-2">
            <Link
              to="/"
              className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl transition-all duration-300 text-gray-300 hover:text-white hover:bg-white/10 font-medium group relative overflow-hidden"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-blue-500/20 to-indigo-500/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              <Home className="h-4 w-4 relative z-10 group-hover:scale-110 transition-transform" />
              <span className="relative z-10">Home</span>
            </Link>
            <Link
              to="/products"
              className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl transition-all duration-300 text-gray-300 hover:text-white hover:bg-white/10 font-medium group relative overflow-hidden"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-blue-500/20 to-indigo-500/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              <Package className="h-4 w-4 relative z-10 group-hover:scale-110 transition-transform" />
              <span className="relative z-10">Products</span>
            </Link>
            <Link
              to="/about"
              className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl transition-all duration-300 text-gray-300 hover:text-white hover:bg-white/10 font-medium group relative overflow-hidden"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-blue-500/20 to-indigo-500/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              <Info className="h-4 w-4 relative z-10 group-hover:scale-110 transition-transform" />
              <span className="relative z-10">About</span>
            </Link>
            <Link
              to="/contact"
              className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl transition-all duration-300 text-gray-300 hover:text-white hover:bg-white/10 font-medium group relative overflow-hidden"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-blue-500/20 to-indigo-500/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              <Mail className="h-4 w-4 relative z-10 group-hover:scale-110 transition-transform" />
              <span className="relative z-10">Contact</span>
            </Link>
          </div>

          <div className="flex items-center space-x-4">
            {user ? (
              <div className="flex items-center space-x-3">
                <Link
                  to="/dashboard"
                  className="flex items-center space-x-2 px-4 py-2.5 text-gray-300 hover:text-white rounded-xl hover:bg-white/10 transition-all duration-300 font-medium group"
                >
                  <User className="h-4 w-4 group-hover:scale-110 transition-transform" />
                  <span>Dashboard</span>
                </Link>
                {user.isAdmin && (
                  <Link
                    to="/admin"
                    className="flex items-center space-x-2 px-4 py-2.5 text-gray-300 hover:text-white rounded-xl hover:bg-white/10 transition-all duration-300 font-medium group"
                  >
                    <Settings className="h-4 w-4 group-hover:scale-110 transition-transform" />
                    <span>Admin</span>
                  </Link>
                )}
                <button
                  onClick={handleLogout}
                  className="flex items-center space-x-2 px-5 py-2.5 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-xl hover:from-red-600 hover:to-red-700 transition-all duration-300 font-semibold transform hover:scale-105 shadow-lg hover:shadow-indigo-500/50"
                >
                  <LogOut className="h-4 w-4" />
                  <span>Logout</span>
                </button>
              </div>
            ) : (
              <div className="flex items-center space-x-3">
                <Link
                  to="/login"
                  className="px-5 py-2.5 text-gray-300 hover:text-white transition-all duration-300 font-medium rounded-xl hover:bg-white/10"
                >
                  Login
                </Link>
                <Link
                  to="/register"
                  className="px-6 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl hover:from-blue-700 hover:to-indigo-700 transition-all duration-300 font-bold transform hover:scale-105 shadow-lg hover:shadow-indigo-500/50 relative overflow-hidden group"
                >
                  <span className="relative z-10">Get Started</span>
                  <div className="absolute inset-0 bg-white/20 translate-y-full group-hover:translate-y-0 transition-transform duration-300"></div>
                </Link>
              </div>
            )}
          </div>
          {/* Mobile Menu Toggle */}
          <div className="md:hidden flex items-center">
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="p-2 text-gray-300 hover:text-white transition-colors"
            >
              {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu Dropdown */}
      {isMobileMenuOpen && (
        <div className="md:hidden absolute top-20 left-0 right-0 bg-neutral-950 border-b border-white/10 shadow-2xl animate-fade-in z-50">
          <div className="px-4 py-6 space-y-4">
            <MobileLink to="/" icon={Home} text="Home" />
            <MobileLink to="/products" icon={Package} text="Products" />
            <MobileLink to="/about" icon={Info} text="About" />
            <MobileLink to="/contact" icon={Mail} text="Contact" />

            <div className="pt-4 border-t border-white/10 space-y-4">
              {user ? (
                <>
                  <MobileLink to="/dashboard" icon={User} text="Dashboard" />
                  {user.isAdmin && (
                    <MobileLink to="/admin" icon={Settings} text="Admin" />
                  )}
                  <button
                    onClick={handleLogout}
                    className="w-full flex items-center space-x-3 px-4 py-3 rounded-xl bg-gradient-to-r from-red-500/20 to-red-600/20 text-indigo-400 hover:text-red-400 border border-red-500/20"
                  >
                    <LogOut className="h-5 w-5" />
                    <span className="font-medium">Logout</span>
                  </button>
                </>
              ) : (
                <div className="grid grid-cols-2 gap-4">
                  <Link
                    to="/login"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="flex justify-center items-center py-3 rounded-xl bg-white/5 border border-white/10 text-white font-medium active:scale-95 transition-transform"
                  >
                    Login
                  </Link>
                  <Link
                    to="/register"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="flex justify-center items-center py-3 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-bold shadow-lg shadow-indigo-500/20 active:scale-95 transition-transform"
                  >
                    Get Started
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

// Footer Component
const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-neutral-950 text-white py-16 border-t border-white/10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-12">
          <div>
            <h3 className="text-2xl font-black mb-4 text-white">AMZProdHunter</h3>
            <p className="text-gray-400 leading-relaxed">Your trusted source for profitable products and data-driven e-commerce solutions.</p>
          </div>
          <div>
            <h4 className="text-lg font-bold mb-4 text-white">Quick Links</h4>
            <ul className="space-y-3">
              <li><a href="/" className="text-gray-500 hover:text-indigo-400 transition-colors duration-300 hover:translate-x-1 inline-block">Home</a></li>
              <li><a href="/products" className="text-gray-500 hover:text-indigo-400 transition-colors duration-300 hover:translate-x-1 inline-block">Products</a></li>
              <li><a href="/about" className="text-gray-500 hover:text-indigo-400 transition-colors duration-300 hover:translate-x-1 inline-block">About</a></li>
              <li><a href="/contact" className="text-gray-500 hover:text-indigo-400 transition-colors duration-300 hover:translate-x-1 inline-block">Contact</a></li>
            </ul>
          </div>
          <div>
            <h4 className="text-lg font-bold mb-4 text-white">Legal</h4>
            <ul className="space-y-3">
              <li><a href="/privacy" className="text-gray-500 hover:text-indigo-400 transition-colors duration-300 hover:translate-x-1 inline-block">Privacy Policy</a></li>
              <li><a href="/security" className="text-gray-500 hover:text-indigo-400 transition-colors duration-300 hover:translate-x-1 inline-block">Security</a></li>
              <li><a href="/terms" className="text-gray-500 hover:text-indigo-400 transition-colors duration-300 hover:translate-x-1 inline-block">Terms</a></li>
              <li><a href="/refunds" className="text-gray-500 hover:text-indigo-400 transition-colors duration-300 hover:translate-x-1 inline-block">Refunds</a></li>
              <li><a href="/payment-policy" className="text-gray-500 hover:text-indigo-400 transition-colors duration-300 hover:translate-x-1 inline-block">Payment Policy</a></li>
            </ul>
          </div>
          <div>
            <h4 className="text-lg font-bold mb-4 text-white">Contact</h4>
            <ul className="space-y-3 text-gray-400">
              <li className="flex items-center space-x-2">
                <Mail className="h-4 w-4 text-indigo-400" />
                <span>muhamed.ahmed100@gmail.com</span>
              </li>
              <li className="flex items-center space-x-2">
                <Phone className="h-4 w-4 text-indigo-400" />
                <span>+92 337 8017144</span>
              </li>
              <li className="flex items-center space-x-2">
                <MapPin className="h-4 w-4 text-indigo-400" />
                <span>Zakariya Hostel NUST, Islamabad</span>
              </li>
            </ul>
          </div>
        </div>
        <div className="border-t border-white/10 pt-8 flex flex-col md:flex-row justify-between items-center">
          <p className="text-gray-500 text-sm">
            &copy; {currentYear} AMZProdHunter. All rights reserved.
          </p>
          <div className="flex space-x-6 mt-4 md:mt-0">
            <a href="#" className="text-gray-500 hover:text-indigo-400 transition-all duration-300 transform hover:scale-110">
              <i className="fab fa-facebook text-xl"></i>
            </a>
            <a href="#" className="text-gray-500 hover:text-indigo-400 transition-all duration-300 transform hover:scale-110">
              <i className="fab fa-twitter text-xl"></i>
            </a>
            <a href="#" className="text-gray-500 hover:text-indigo-400 transition-all duration-300 transform hover:scale-110">
              <i className="fab fa-instagram text-xl"></i>
            </a>
            <a href="#" className="text-gray-500 hover:text-indigo-400 transition-all duration-300 transform hover:scale-110">
              <i className="fab fa-linkedin text-xl"></i>
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};


// Product Card Component
const ProductCard = ({ product, onPurchase }) => {
  const { purchaseProduct, globalSoldProducts } = useProducts();
  const navigate = useNavigate();
  const { user } = useAuth();

  // Product is sold if it's a dummy, or if ANY user has purchased it (global sold status)
  const isSold = product.sold || globalSoldProducts.includes(product.id);
  const forceDisablePurchases = true;
  const isPurchaseDisabled = isSold || forceDisablePurchases;

  const handleViewDetails = () => {
    if (isSold && !product.sold) return; // Only allow details for real purchased products in dashboard, not in catalog
    navigate(`/products/${product.id}`);
  };

  const handlePurchase = async () => {
    if (isSold) return;
    if (!user) {
      navigate('/login');
      return;
    }
    await purchaseProduct(product.id);
    navigate('/dashboard'); // Redirect immediately after purchase
  };

  const pnLabel = getProductPnLabel(product);
  const whatsappPurchaseMessage = encodeURIComponent(
    `Hello, can i get information about PN Number ${pnLabel}?`
  );
  const whatsappPurchaseLink = `https://wa.me/${WHATSAPP_NUMBER}?text=${whatsappPurchaseMessage}`;

  return (
    <div className={`group relative bg-neutral-900/40 backdrop-blur-md rounded-2xl shadow-2xl transition-all duration-500 overflow-hidden border border-white/5 hover:border-indigo-500/50 transform hover:-translate-y-2 hover:shadow-[0_20px_60px_-15px_rgba(79,70,229,0.4)] h-[550px] sm:h-[600px] flex flex-col ${isSold ? 'grayscale opacity-75' : ''}`}>
      {/* Gradient overlay on hover */}
      <div className="absolute inset-0 bg-gradient-to-br from-indigo-500/10 to-blue-600/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"></div>

      {/* Image/Icon Section */}
      {/* Image/Icon Section */}
      <div className="relative w-full h-[220px] sm:h-[280px] bg-[#080808] flex items-center justify-center border-b border-white/5 overflow-hidden transition-colors">

        {/* Subtle Static Vignette - No Hover Brightness */}
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_rgba(255,255,255,0.03)_0%,_transparent_70%)]"></div>

        <div className="relative z-10 transform group-hover:scale-110 transition-transform duration-500">
          {isSold ? (
            <div className="relative transform transition-all duration-500 hover:scale-105" style={{ transformStyle: 'preserve-3d', perspective: '1000px' }}>
              {/* Sold Out Card Style */}
              <div className="w-48 h-32 bg-neutral-800 rounded-xl border border-white/5 flex flex-col items-center justify-center relative overflow-hidden grayscale opacity-50">
                <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/carbon-fibre.png')] opacity-20"></div>
                <span className="text-4xl font-black text-white/10 tracking-widest z-10">SOLD</span>
              </div>

              <div className="absolute -top-4 -right-4 bg-gradient-to-r from-red-500 to-red-600 text-white font-bold text-sm px-4 py-2 rounded-full shadow-lg transform rotate-12 z-20">
                SOLD OUT
              </div>
            </div>
          ) : (
            <div className="relative group/icon perspective-container flex items-center justify-center w-full h-full" style={{ perspective: '1000px' }}>
              {/* 3D Floating Product Card */}
              <div
                className="relative w-72 h-40 transition-all duration-700 ease-out transform group-hover:rotate-x-0 group-hover:rotate-y-0 group-hover:scale-110 rotate-x-6 rotate-y-6"
                style={{
                  transformStyle: 'preserve-3d',
                  transform: 'rotateX(10deg) rotateY(-10deg) rotateZ(2deg)'
                }}
              >
                {/* Card Face */}
                <div className="absolute inset-0 bg-gradient-to-br from-neutral-900 via-black to-neutral-900 rounded-xl border border-white/10 shadow-2xl flex flex-col justify-between overflow-hidden">

                  {/* Subtle Grid Texture */}
                  <div className="absolute inset-0 opacity-20 pointer-events-none" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(255,255,255,0.15) 1px, transparent 0)', backgroundSize: '10px 10px' }}></div>

                  {/* Top Content (Centered) */}
                  <div className="relative z-10 flex-1 flex flex-col items-center justify-center pt-4">
                    <div className="flex items-center space-x-2 text-indigo-400 mb-1">
                      <div className="w-1.5 h-1.5 rounded-full bg-indigo-500 animate-pulse"></div>
                      <span className="text-[9px] font-bold tracking-[0.2em] uppercase">Premium Asset</span>
                    </div>

                    <h4 className="text-3xl font-black text-white tracking-wider font-mono">
                      {pnLabel}
                    </h4>

                    {/* Decorative Barcode-like lines */}
                    <div className="mt-2 flex space-x-1 opacity-40">
                      <div className="w-0.5 h-3 bg-white/50"></div>
                      <div className="w-1.5 h-3 bg-white/50"></div>
                      <div className="w-0.5 h-3 bg-white/50"></div>
                      <div className="w-2 h-3 bg-white/50"></div>
                      <div className="w-0.5 h-3 bg-white/50"></div>
                    </div>
                  </div>

                  {/* Footer (Spaced Out) */}
                  <div className="relative z-10 w-full flex justify-between items-end px-4 pb-3">
                    {/* Left: Amazon */}
                    <div className="flex items-center gap-1.5 opacity-60">
                      <span className="text-[7px] font-bold text-white tracking-widest uppercase">Amazon</span>
                      <div className="flex space-x-0.5">
                        <div className="w-5 h-1 bg-white/60 rounded-[0.5px]"></div>
                        <div className="w-1 h-1 bg-white/60 rounded-[0.5px]"></div>
                      </div>
                    </div>

                    {/* Right: Distributor */}
                    <div className="flex items-center gap-1.5 opacity-60">
                      <span className="text-[7px] font-bold text-white tracking-widest uppercase">Distributor</span>
                      <div className="flex space-x-0.5">
                        <div className="w-5 h-1 bg-white/60 rounded-[0.5px]"></div>
                        <div className="w-1 h-1 bg-white/60 rounded-[0.5px]"></div>
                      </div>
                    </div>
                  </div>

                  {/* Glossy Glare Effect */}
                  <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-white/5 to-transparent pointer-events-none"></div>
                </div>

                {/* 3D Depth/Shadow Layer */}
                <div className="absolute inset-0 bg-indigo-500/20 rounded-xl blur-xl transform translate-z-[-20px] scale-90 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              </div>
            </div>
          )}
        </div>

        {/* Margin Badge */}
        {!isSold && (
          <div className="absolute top-2 right-2 sm:top-4 sm:right-4">
            <div className="bg-neutral-900 border border-white/20 text-white px-3 py-1.5 sm:px-4 sm:py-2 rounded-full font-bold text-xs sm:text-sm shadow-lg flex items-center space-x-1">
              <TrendingUp className="h-3 w-3 sm:h-4 sm:w-4" />
              <span>{product.margin?.toFixed(1) || 0}% Margin</span>
            </div>
          </div>
        )}
      </div>

      {/* Content Section */}
      <div className="relative z-10 p-4 sm:p-6 flex-1 flex flex-col justify-between bg-transparent">
        <div>
          <h3 className="text-lg sm:text-xl font-bold text-white mb-2 sm:mb-3 group-hover:text-indigo-400 transition-colors duration-300 truncate">
            {product.title.split(' ')[0] + (product.title.split(' ').length > 1 ? '...' : '')}
          </h3>

          <div className="flex items-center justify-between mb-3 sm:mb-4">
            <div>
              <span className="text-2xl sm:text-3xl font-black text-white" style={{ textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000, 3px 3px 0 #000' }}>
                ${product.price}
              </span>
            </div>
            <div className="flex items-center text-yellow-400 text-xs sm:text-sm">
              <Star className="h-3 w-3 sm:h-4 sm:w-4 mr-1 fill-current" />
              <span className="font-semibold text-white">{product.rating}</span>
              <span className="text-gray-500 ml-1">({product.reviews})</span>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col space-y-2 sm:space-y-3">
          <button
            onClick={handleViewDetails}
            className={`group/btn relative w-full py-2.5 sm:py-3.5 rounded-xl font-bold text-sm sm:text-base transition-all duration-300 flex items-center justify-center overflow-hidden border ${isSold
              ? 'bg-neutral-800 border-white/5 text-gray-600 cursor-not-allowed'
              : 'bg-transparent border-white/20 text-white hover:bg-white/5 hover:border-white/40 shadow-sm'
              }`}
            disabled={isSold}
          >
            <span className="relative z-10 flex items-center">
              <Eye className="h-4 w-4 sm:h-5 sm:w-5 mr-1.5 sm:mr-2 text-white transition-colors" />
              <span className="hidden sm:inline">View Details</span>
              <span className="sm:hidden">Details</span>
            </span>
          </button>

          <a
            href={whatsappPurchaseLink}
            target="_blank"
            rel="noopener noreferrer"
            className="relative w-full py-2.5 sm:py-3.5 rounded-xl font-bold text-sm sm:text-base transition-all duration-300 flex items-center justify-center overflow-hidden bg-gradient-to-r from-gray-800/80 to-gray-700/80 backdrop-blur-md border border-white/10 hover:from-gray-700/90 hover:to-gray-600/90 text-white shadow-lg shadow-gray-900/20 transform hover:scale-[1.02]"
            aria-label="Purchase through WhatsApp"
          >
            <img
              src="https://upload.wikimedia.org/wikipedia/commons/6/6b/WhatsApp.svg"
              alt="WhatsApp"
              className="h-4 w-4 sm:h-5 sm:w-5 mr-1.5 sm:mr-2 brightness-0 invert"
              loading="lazy"
            />
            <span className="hidden sm:inline">Purchase through WhatsApp</span>
            <span className="sm:hidden">WhatsApp</span>
          </a>
        </div>
      </div>
    </div>
  );
};

// Home Page Component
const HomePage = () => {
  const { products, fetchProducts, isLoading, globalSoldProducts } = useProducts();

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  // Get top 3 products by margin (highest margin first), only from available products
  const availableProducts = products.filter(p => !globalSoldProducts.includes(p.id));
  const topProducts = availableProducts
    .sort((a, b) => (b.margin || 0) - (a.margin || 0))
    .slice(0, 3);

  return (
    <div className="flex-1 flex flex-col bg-neutral-950 text-white">
      <HeroSection />

      {/* Top Products Section */}
      <section className="py-24 relative bg-neutral-950">
        {/* Grid background for this section */}
        <div className="absolute inset-0 opacity-[0.15] pointer-events-none" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='80' height='80' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M 80 0 L 0 0 0 80' fill='none' stroke='rgba(99,102,241,0.3)' stroke-width='1'/%3E%3C/svg%3E")`,
          backgroundSize: '80px 80px',
          backgroundAttachment: 'fixed'
        }}></div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-black text-white mb-4">
              Discover Our <span className="bg-gradient-to-r from-blue-400 via-indigo-500 to-violet-500 bg-clip-text text-transparent">Top Picks</span>
            </h2>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              Hand-picked products with proven profitability and market potential
            </p>
          </div>

          {isLoading ? (
            <div className="text-center py-20">
              <div className="inline-flex items-center space-x-3 text-indigo-400 text-xl">
                <div className="w-3 h-3 bg-indigo-500 rounded-full animate-bounce"></div>
                <div className="w-3 h-3 bg-indigo-500 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                <div className="w-3 h-3 bg-indigo-500 rounded-full animate-bounce" style={{ animationDelay: '0.4s' }}></div>
                <span className="ml-4 font-semibold">Loading products...</span>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {topProducts.map(product => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
          )}

          <div className="text-center mt-16">
            <Link
              to="/products"
              className="group inline-flex items-center px-10 py-5 bg-gradient-to-r from-indigo-800/85 to-blue-800/85 backdrop-blur-md border border-indigo-500/20 text-white text-lg font-bold rounded-xl shadow-2xl shadow-indigo-900/30 hover:from-indigo-700/95 hover:to-blue-700/95 hover:border-indigo-400/40 hover:shadow-indigo-500/50 transform hover:scale-105 transition-all duration-300"
            >
              View All Products
              <ChevronRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 relative bg-neutral-950">
        {/* Grid background for this section */}
        <div className="absolute inset-0 opacity-[0.15] pointer-events-none" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='80' height='80' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M 80 0 L 0 0 0 80' fill='none' stroke='rgba(99,102,241,0.3)' stroke-width='1'/%3E%3C/svg%3E")`,
          backgroundSize: '80px 80px'
        }}></div>
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-5xl font-black mb-6 text-white">
            Ready to <span className="bg-gradient-to-r from-blue-400 via-indigo-500 to-violet-500 bg-clip-text text-transparent">Scale Your Business?</span>
          </h2>
          <p className="text-xl text-gray-400 mb-10 leading-relaxed">
            Join thousands of successful entrepreneurs using AMZProdHunter to discover profitable products and maximize their margins
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Link
              to="/register"
              className="group inline-flex items-center px-12 py-6 bg-gradient-to-r from-indigo-800/85 to-blue-800/85 backdrop-blur-md border border-indigo-500/20 text-white text-lg font-black rounded-xl shadow-2xl shadow-indigo-900/30 hover:from-indigo-700/95 hover:to-blue-700/95 hover:border-indigo-400/40 hover:shadow-indigo-500/50 transform hover:scale-105 transition-all duration-300"
            >
              Get Started Now
              <ArrowRight className="ml-3 h-6 w-6 group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link
              to="/payment-policy"
              className="inline-flex items-center px-8 py-6 border-2 border-white/20 text-white text-lg font-semibold rounded-xl hover:bg-white/10 hover:border-white/40 transition-all duration-300"
            >
              Payment Policy
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

// Products Page Component
const ProductsPage = () => {
  const { products, categories, filters, updateFilters, fetchProducts, isLoading, error, allProducts, globalSoldProducts, currentPage, setCurrentPage } = useProducts();
  const { user } = useAuth();
  const navigate = useNavigate();
  const productsPerPage = 9;

  // Dummy sold products
  const dummySoldProducts = Array.from({ length: 55 }, (_, i) => {
    const margin = (Math.random() * 30 + 10).toFixed(1); // 10.0 - 40.0
    const price = (10 + Math.random() * 30).toFixed(2); // 10.00 - 40.00
    return {
      id: `sold-dummy-${i + 1}`,
      title: `Sold Product #${i + 1}`,
      price,
      margin,
      summary: 'This product has been sold',
      description: 'This product has been sold',
      rating: (Math.random() * 2 + 3).toFixed(1), // 3.0 - 5.0
      reviews: Math.floor(Math.random() * 500 + 20),
      imageUrl: '',
      sold: true,
    };
  });

  // Combine real and dummy products for display
  // Sort so available products come first, sold products at the end
  const sortedProducts = [...products].sort((a, b) => {
    const aSold = globalSoldProducts.includes(a.id);
    const bSold = globalSoldProducts.includes(b.id);
    if (aSold === bSold) return 0;
    return aSold ? 1 : -1;
  });
  const isAnyFilterActive =
    filters.category ||
    (filters.search && filters.search.trim() !== '') ||
    (filters.priceRange && (filters.priceRange[0] !== 0 || filters.priceRange[1] !== 1000));

  const allDisplayProducts = isAnyFilterActive
    ? sortedProducts
    : [...sortedProducts, ...dummySoldProducts];

  const totalPages = Math.ceil(allDisplayProducts.length / productsPerPage);

  const startIdx = (currentPage - 1) * productsPerPage;
  const endIdx = startIdx + productsPerPage;
  const paginatedProducts = allDisplayProducts.slice(startIdx, endIdx);

  // Reset to page 1 when filters change


  // Scroll to top when page changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentPage]);
  // Ensure filters are applied when component mounts or when allProducts change

  // Ensure filters are applied when component mounts or when allProducts change



  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    updateFilters({ [name]: value });
  };

  const handlePriceRangeChange = (e) => {
    const { name, value } = e.target;
    const newRange = [...filters.priceRange];
    if (name === 'minPrice') newRange[0] = Number(value);
    if (name === 'maxPrice') newRange[1] = Number(value);
    updateFilters({ priceRange: newRange });
  };

  // Log products for debugging
  console.log('[ProductsPage] Products in UI:', products);

  if (isLoading) return <div className="text-center py-20 text-xl text-light-gray-text animate-pulse">Loading products...</div>;
  if (error) return <div className="text-center py-20 text-xl text-rose-500">Error: {error}</div>;

  return (
    <div className="min-h-screen bg-neutral-950 pt-32 pb-20 relative overflow-hidden">
      {/* Bigger Grid Pattern - Same as About Page */}
      <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iODAiIGhlaWdodD0iODAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI4MCIgaGVpZ2h0PSI4MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAwIDQwIEwgODAgNDAgTSA0MCAwIEwgNDAgODAiIGZpbGw9Im5vbmUiIHN0cm9rZT0icmdiYSgyNTUsMjU1LDI1NSwwLjAzKSIgc3Ryb2tlLXdpZHRoPSIxIi8+PC9wYXR0ZXJuPjwvZGVmcz48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSJ1cmwoI2dyaWQpIi8+PC9zdmc+')] opacity-50"></div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12 relative z-10">
        <div className="text-center mb-16">
          <h1 className="text-4xl md:text-5xl font-black mb-4 text-white">
            Product <span className="bg-gradient-to-r from-blue-400 via-indigo-500 to-violet-500 bg-clip-text text-transparent">Catalog</span>
          </h1>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto">
            Browse our curated collection of high-potential products
          </p>
        </div>

        {/* Filters Section */}
        <div className="relative bg-neutral-900 p-8 rounded-2xl shadow-2xl mb-8 grid grid-cols-1 md:grid-cols-4 gap-8 border border-white/10 backdrop-blur-xl">
          {/* Search Filter */}
          <div className="group relative z-10">
            <label htmlFor="search" className="block text-sm font-semibold text-gray-400 mb-3 group-hover:text-indigo-400 transition-colors duration-200">Search Products</label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-gray-500 group-hover:text-indigo-400 transition-colors duration-200">
                <Search className="h-5 w-5" />
              </div>
              <input
                type="text"
                id="search"
                name="search"
                value={filters.search}
                onChange={handleFilterChange}
                className="block w-full pl-12 pr-4 py-3 border-2 border-white/10 rounded-xl bg-neutral-950 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm transition-all duration-200 hover:border-white/20"
                placeholder="Search by name, PN number, or description..."
              />
            </div>
          </div>

          {/* Category Filter */}
          <div className="group relative z-10">
            <label htmlFor="category" className="block text-sm font-semibold text-gray-400 mb-3 group-hover:text-indigo-400 transition-colors duration-200">Category</label>
            <div className="relative">
              <select
                id="category"
                name="category"
                value={filters.category}
                onChange={handleFilterChange}
                className="block w-full pl-4 pr-10 py-3 border-2 border-white/10 rounded-xl bg-neutral-950 text-white focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm transition-all duration-200 appearance-none hover:border-white/20 cursor-pointer"
              >
                <option value="" className="bg-neutral-950">All Categories</option>
                {categories.map(category => (
                  <option key={category} value={category} className="bg-neutral-950">{category}</option>
                ))}
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-gray-500 group-hover:text-indigo-400 transition-colors duration-200">
                <ChevronRight className="h-5 w-5 rotate-90" />
              </div>
            </div>
          </div>

          {/* Sort By Filter */}
          <div className="group">
            <label htmlFor="sortBy" className="block text-sm font-semibold text-gray-400 mb-3 group-hover:text-indigo-400 transition-colors duration-200">Sort By</label>
            <div className="relative">
              <select
                id="sortBy"
                name="sortBy"
                value={filters.sortBy}
                onChange={handleFilterChange}
                className="block w-full pl-4 pr-10 py-3 border-2 border-white/10 rounded-xl bg-neutral-950 text-white focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm transition-all duration-200 appearance-none hover:border-white/20 cursor-pointer"
              >
                <option value="price-desc" className="bg-neutral-950">Price (High to Low)</option>
                <option value="price" className="bg-neutral-950">Price (Low to High)</option>
                <option value="margin" className="bg-neutral-950">Profit Margin</option>
                <option value="profit" className="bg-neutral-950">Profit</option>
                <option value="roi" className="bg-neutral-950">ROI</option>
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-gray-500 group-hover:text-indigo-400 transition-colors duration-200">
                <ChevronRight className="h-5 w-5 rotate-90" />
              </div>
            </div>
          </div>

          {/* Price Range Filter */}
          <div className="group">
            <label className="block text-sm font-semibold text-gray-400 mb-3 group-hover:text-indigo-400 transition-colors duration-200">Price Range</label>
            <div className="flex items-center space-x-3">
              <div className="relative flex-1">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-500 group-hover:text-indigo-400 transition-colors duration-200">
                  <DollarSign className="h-4 w-4" />
                </div>
                <input
                  type="number"
                  name="minPrice"
                  value={filters.priceRange[0] || ''}
                  onChange={handlePriceRangeChange}
                  className="block w-full pl-8 pr-3 py-3 border-2 border-white/10 rounded-xl bg-neutral-950 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm transition-all duration-200 hover:border-white/20"
                  placeholder="Min"
                />
              </div>
              <span className="text-gray-500">-</span>
              <div className="relative flex-1">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-500 group-hover:text-indigo-400 transition-colors duration-200">
                  <DollarSign className="h-4 w-4" />
                </div>
                <input
                  type="number"
                  name="maxPrice"
                  value={filters.priceRange[1] || ''}
                  onChange={handlePriceRangeChange}
                  className="block w-full pl-8 pr-3 py-3 border-2 border-white/10 rounded-xl bg-neutral-950 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm transition-all duration-200 hover:border-white/20"
                  placeholder="Max"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Product Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 gap-8 mb-10">
          {paginatedProducts.length > 0 ? (
            paginatedProducts.map(product => (
              <ProductCard key={product.id} product={product} />
            ))
          ) : (
            <div className="col-span-full text-center py-10 text-gray-400 text-lg">
              No products found matching your criteria.
            </div>
          )}
        </div>

        {/* Pagination Controls */}
        {totalPages > 1 && (
          <div className="flex justify-center items-center mt-20 space-x-2">
            <button
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className={`px-6 py-3 rounded-xl font-bold transition-all duration-200 backdrop-blur-md border ${currentPage === 1 ? 'bg-gray-800/30 border-gray-700/20 text-gray-600 cursor-not-allowed' : 'bg-gradient-to-r from-blue-600/50 to-indigo-600/50 border-indigo-500/20 text-white hover:from-blue-600/70 hover:to-indigo-600/70 hover:border-indigo-400/30 shadow-lg hover:shadow-indigo-500/50'}`}
            >
              Previous
            </button>
            {Array.from({ length: totalPages }, (_, i) => (
              <button
                key={i + 1}
                onClick={() => setCurrentPage(i + 1)}
                className={`px-4 py-3 rounded-xl font-bold transition-all duration-200 backdrop-blur-md border ${currentPage === i + 1 ? 'bg-gradient-to-r from-blue-600/50 to-indigo-600/50 border-indigo-500/20 text-white shadow-lg' : 'bg-neutral-900/40 border-neutral-700/20 text-gray-400 hover:bg-neutral-800/60 hover:border-neutral-600/30 hover:text-white'}`}
              >
                {i + 1}
              </button>
            ))}
            <button
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className={`px-6 py-3 rounded-xl font-bold transition-all duration-200 backdrop-blur-md border ${currentPage === totalPages ? 'bg-gray-800/30 border-gray-700/20 text-gray-600 cursor-not-allowed' : 'bg-gradient-to-r from-blue-600/50 to-indigo-600/50 border-indigo-500/20 text-white hover:from-blue-600/70 hover:to-indigo-600/70 hover:border-indigo-400/30 shadow-lg hover:shadow-indigo-500/50'}`}
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// Product Detail Page Component
const ProductDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { purchaseProduct, globalSoldProducts } = useProducts();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);


  useEffect(() => {
    console.log(`[ProductDetailPage] useEffect triggered for product ID: ${id}`);
    const getProductDetails = async () => {
      try {
        setLoading(true);
        setError(null); // Clear previous errors
        console.log(`[ProductDetailPage] Calling APIService.fetchProductDetails(${id})`);
        const fetchedProduct = await APIService.fetchProductDetails(id, user?.id, user?.email, user?.is_admin);
        console.log(`[ProductDetailPage] APIService.fetchProductDetails(${id}) returned:`, fetchedProduct);
        if (fetchedProduct) {
          setProduct(fetchedProduct);
          console.log(`[ProductDetailPage] Product details set:`, fetchedProduct);
        } else {
          setError('Product not found or empty response.');
          console.log(`[ProductDetailPage] Product not found for ID: ${id}`);
        }
      } catch (err) {
        setError(err.message);
        console.error(`[ProductDetailPage] Error in getProductDetails:`, err);
      } finally {
        setLoading(false);
        console.log(`[ProductDetailPage] Loading set to false.`);
      }
    };
    if (id) { // Only fetch if ID is available
      getProductDetails();
    } else {
      setError("Product ID is missing.");
      setLoading(false);
    }
  }, [id]);

  const handlePurchase = async () => {
    console.log(`[ProductDetailPage] Purchase button clicked for product:`, product);
    if (!user) {
      console.log("[ProductDetailPage] User not logged in, navigating to login.");
      navigate('/login');
      return;
    }
    if (!product) {
      console.error("[ProductDetailPage] Attempted to purchase, but product data is null.");
      return;
    }
    console.log(`[ProductDetailPage] Calling purchaseProduct for product ID: ${product.id}`);
    await purchaseProduct(product.id);

    // Refresh product data to show full details after purchase
    try {
      console.log(`[ProductDetailPage] Refreshing product data after purchase for ID: ${product.id}`);
      const refreshedProduct = await APIService.fetchProductDetails(product.id, user.id, user.email, user.is_admin);
      if (refreshedProduct) {
        setProduct(refreshedProduct);
        console.log(`[ProductDetailPage] Product data refreshed with full details:`, refreshedProduct);
      }
    } catch (error) {
      console.error(`[ProductDetailPage] Error refreshing product data:`, error);
    }

    // Redirect to dashboard after a brief delay to show the updated data
    setTimeout(() => {
      navigate('/dashboard');
    }, 1000);
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-neutral-950 relative overflow-hidden">
      {/* Grid background */}
      <div className="absolute inset-0 opacity-[0.08]" style={{
        backgroundImage: `url("data:image/svg+xml,%3Csvg width='80' height='80' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M 80 0 L 0 0 0 80' fill='none' stroke='rgba(99,102,241,0.3)' stroke-width='1'/%3E%3C/svg%3E")`,
        backgroundSize: '80px 80px'
      }}></div>

      {/* Loading content */}
      <div className="relative z-10 text-center">
        <div className="inline-flex items-center space-x-3 text-white text-2xl mb-4">
          <div className="w-3 h-3 bg-indigo-500 rounded-full animate-bounce"></div>
          <div className="w-3 h-3 bg-indigo-500 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
          <div className="w-3 h-3 bg-indigo-500 rounded-full animate-bounce" style={{ animationDelay: '0.4s' }}></div>
        </div>
        <p className="text-xl font-semibold text-white">Loading</p>
      </div>
    </div>
  );
  if (error) return <div className="min-h-screen flex items-center justify-center text-xl text-rose-400 bg-neutral-950">Error: {error}</div>;
  if (!product) return <div className="min-h-screen flex items-center justify-center text-xl text-gray-400 bg-neutral-950">Product not found.</div>;

  // Check if the current user has purchased this product (for access to details)
  // Admin override: Check both email and is_admin field from Firebase
  const isAdmin = user?.is_admin === true || user?.email?.toLowerCase() === ADMIN_EMAIL.toLowerCase();
  const hasPurchased = isAdmin || (user && user.purchasedProducts.includes(product.id));
  // Check if the product is sold globally (by any user)
  const isSoldGlobally = globalSoldProducts.includes(product.id);
  const forceDisablePurchases = true;
  const pnLabel = getProductPnLabel(product);
  const whatsappPurchaseMessage = encodeURIComponent(
    `Hello, can i get information about PN Number ${pnLabel}?`
  );
  const whatsappPurchaseLink = `https://wa.me/${WHATSAPP_NUMBER}?text=${whatsappPurchaseMessage}`;

  // Debug log to check if hasPurchased is working
  console.log('[ProductDetailPage] isAdmin:', isAdmin, 'user?.is_admin:', user?.is_admin, 'user?.email:', user?.email, 'ADMIN_EMAIL:', ADMIN_EMAIL);
  console.log('[ProductDetailPage] hasPurchased:', hasPurchased, 'user:', user?.id, 'product.id:', product.id, 'user.purchasedProducts:', user?.purchasedProducts);
  console.log('[ProductDetailPage] isSoldGlobally:', isSoldGlobally, 'globalSoldProducts:', globalSoldProducts);

  return (
    <div className="bg-neutral-950 text-white min-h-screen flex items-center justify-center relative overflow-hidden">
      {/* Clean gradient */}
      <div className="absolute inset-0 bg-gradient-to-b from-neutral-950 via-neutral-900 to-neutral-950 opacity-30"></div>

      {/* Enhanced visible grid - Reverted Opacity */}
      <div className="absolute inset-0 opacity-[0.12]" style={{
        backgroundImage: `url("data:image/svg+xml,%3Csvg width='80' height='80' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M 80 0 L 0 0 0 80' fill='none' stroke='rgba(100,180,255,0.3)' stroke-width='1'/%3E%3C/svg%3E")`,
        backgroundSize: '80px 80px'
      }}></div>

      {/* Animated lighting effects - Reduced Opacity */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-1/4 w-[700px] h-[700px] rounded-full filter blur-[150px] animate-pulse" style={{ background: 'radial-gradient(circle, rgba(59,130,246,0.04) 0%, rgba(59,130,246,0.02) 40%, transparent 70%)', animationDuration: '4s' }}></div>
        <div className="absolute bottom-20 right-1/4 w-[700px] h-[700px] rounded-full filter blur-[150px] animate-pulse" style={{ background: 'radial-gradient(circle, rgba(34,211,238,0.04) 0%, rgba(34,211,238,0.02) 40%, transparent 70%)', animationDelay: '1s', animationDuration: '4s' }}></div>
      </div>



      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-center w-full" style={{ minHeight: '70vh' }}>
        <div className="w-full bg-neutral-900 backdrop-blur-xl rounded-2xl shadow-2xl p-6 border border-white/10" style={{ marginTop: '60px' }}>
          <button
            onClick={() => navigate('/products')}
            className="inline-flex items-center text-indigo-400 hover:text-cyan-300 mb-2 transition-colors duration-200 text-sm"
          >
            <ChevronRight className="h-4 w-4 rotate-180 mr-1" /> Back to Products
          </button>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-start" style={{ minHeight: '60vh' }}>
            {/* Left column: Image + Product Codes/Details */}
            <div className="flex flex-col items-center">
              <div className="relative flex justify-center items-center p-2 border-2 border-indigo-500/20 rounded-lg shadow-lg shadow-indigo-500/5 bg-white/5 max-w-[320px] max-h-[320px] mx-auto mb-2">
                <div className={`${!hasPurchased ? 'blur-xl pointer-events-none select-none' : ''} w-full h-full flex items-center justify-center`}>
                  {product.image_url ? (
                    <img
                      src={product.image_url}
                      alt={product.title}
                      className="max-w-[300px] max-h-[300px] w-auto h-auto rounded-md object-contain"
                      onError={(e) => {
                        e.target.src = 'https://via.placeholder.com/300x300/38BDF8/000000?text=Product+Image';
                      }}
                    />
                  ) : (
                    <div className="w-[200px] h-[200px] bg-gradient-to-br from-neutral-900 to-neutral-800 flex items-center justify-center rounded-md">
                      <Package className="h-20 w-20 text-indigo-400" />
                    </div>
                  )}
                </div>
                {!hasPurchased && (
                  <div className="absolute inset-0 bg-gray-800/90 rounded-md flex items-center justify-center">
                    <p className="text-white text-center text-sm font-semibold px-4">
                      Image and other details available after purchase
                    </p>
                  </div>
                )}
              </div>
              {/* Product Codes/Details section under image */}
              <div className="w-full mb-2 p-3 bg-white/5 rounded-xl border border-white/10">
                <h3 className="text-base font-semibold text-white mb-2 flex items-center">
                  <Package className="h-4 w-4 mr-1 text-indigo-400" />
                  Product Codes
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                  <div className="text-center p-2 bg-black/30 rounded-lg border border-white/5">
                    <p className="text-[10px] text-gray-400 uppercase tracking-wide mb-0.5">ASIN</p>
                    <p className={`font-mono font-semibold text-white text-sm ${!hasPurchased ? 'blur-[1.2px] select-none' : ''}`}>
                      {hasPurchased ? (product.asin ? (Array.isArray(product.asin) ? product.asin[0] : product.asin) : 'N/A') : '🔒 Purchase Required'}
                    </p>
                  </div>
                  <div className="text-center p-2 bg-black/30 rounded-lg border border-white/5">
                    <p className="text-[10px] text-gray-400 uppercase tracking-wide mb-0.5">UPC</p>
                    <p className={`font-mono font-semibold text-white text-sm ${!hasPurchased ? 'blur-[1.2px] select-none' : ''}`}>
                      {hasPurchased ? (product.upc ? (Array.isArray(product.upc) ? product.upc[0] : product.upc) : 'N/A') : '🔒 Purchase Required'}
                    </p>
                  </div>
                  <div className="text-center p-2 bg-black/30 rounded-lg border border-white/5">
                    <p className="text-[10px] text-gray-400 uppercase tracking-wide mb-0.5">EAN</p>
                    <p className={`font-mono font-semibold text-white text-sm ${!hasPurchased ? 'blur-[1.2px] select-none' : ''}`}>
                      {hasPurchased ? (product.ean ? (Array.isArray(product.ean) ? product.ean[0] : product.ean) : 'N/A') : '🔒 Purchase Required'}
                    </p>
                  </div>
                </div>
                {/* Additional Product Info (model, color, etc.) */}
                <div className="grid grid-cols-2 gap-2 text-xs mt-2">
                  <div>
                    <p className="text-gray-400">Model</p>
                    <p className={`font-semibold text-white ${!hasPurchased ? 'blur-[1.2px] select-none' : ''}`}>
                      {hasPurchased ? (product.model || 'N/A') : '🔒 Purchase Required'}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-400">Color</p>
                    <p className="font-semibold text-white">
                      {product.color || 'N/A'}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-400">Target Audience</p>
                    <p className="font-semibold text-white">
                      {product.target_audience || 'N/A'}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-400">HazMat</p>
                    <p className="font-semibold text-white">
                      {product.is_hazmat !== undefined ? (product.is_hazmat ? 'Yes' : 'No') : 'N/A'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Analytics Metrics - Moved to left column */}
              {((product.bought_past_month && product.bought_past_month > 0) || (product.new_3rd_party_fba_stock && product.new_3rd_party_fba_stock > 0)) && (
                <div className="w-full mb-2 p-3 bg-white/5 rounded-xl border border-white/10">
                  <h3 className="text-base font-semibold text-white mb-2">Analytics</h3>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div>
                      <p className="text-gray-400">Demand vs Supply</p>
                      <p className="font-semibold text-white">
                        {(() => {
                          const bought = product.bought_past_month || 0;
                          const stock = product.new_3rd_party_fba_stock || 0;
                          const difference = bought - stock;
                          if (difference > 0) {
                            return `${difference.toLocaleString()} more demand than supply`;
                          } else if (difference < 0) {
                            return `${Math.abs(difference).toLocaleString()} more supply than demand`;
                          } else {
                            return 'Demand equals supply';
                          }
                        })()}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Right column: All other product info */}
            <div className="overflow-y-auto max-h-[70vh] pr-2">
              <h1 className={`text-2xl font-bold text-white mb-2 leading-tight ${!hasPurchased ? 'select-none' : ''}`} style={!hasPurchased ? { filter: 'blur(1.2px)' } : {}}>
                {hasPurchased ? product.title : '🔒 Product Title - Purchase Required'}
              </h1>
              <p className="text-xs font-mono text-gray-400 tracking-widest uppercase mb-2">
                PN Number: <span className="text-white">{pnLabel}</span>
              </p>
              <div className="mb-3">
                <p className="text-sm text-white">Category: {product.category || product.categories_root || 'Electronics'}</p>
              </div>
              {/* Price and Profit Metrics */}
              <div className="flex flex-wrap items-center space-x-3 mb-2">
                <span className="text-xl font-extrabold text-white" style={{ textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000, 3px 3px 0 #000' }}>
                  ${(product.dprice || product.price || 0).toFixed(2)}
                </span>
                <span className="text-sm font-semibold text-emerald-500 flex items-center animate-text-glow-pulse">
                  <BarChart3 className="h-3 w-3 mr-1" />
                  Margin: {(product.margin_pct || product.margin || 0).toFixed(1)}%
                </span>
                {product.profit && (
                  <span className="text-xs font-semibold text-emerald-500 flex items-center">
                    <DollarSign className="h-3 w-3 mr-1" />
                    Profit: ${(parseFloat(product.profit) || 0).toFixed(2)}
                  </span>
                )}
                {product.roi_pct && (
                  <span className="text-xs font-semibold text-emerald-500 flex items-center">
                    <TrendingUp className="h-3 w-3 mr-1" />
                    ROI: {(parseFloat(product.roi_pct) || 0).toFixed(1)}%
                  </span>
                )}
              </div>

              {/* Sales Performance */}
              <div className="grid grid-cols-2 gap-3 mb-2 p-3 bg-white/5 rounded-xl border border-white/10">
                <div>
                  <p className="text-xs text-gray-400">Sales Rank (90d avg)</p>
                  <p className="text-sm font-semibold text-white">
                    {product.sales_rank_90_days_avg ? `#${product.sales_rank_90_days_avg.toLocaleString()}` : 'N/A'}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-400">Bought past month</p>
                  <p className="text-sm font-semibold text-white">
                    {product.bought_past_month ? product.bought_past_month.toLocaleString() : 'N/A'}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-400">Reviews</p>
                  <div className="flex items-center">
                    <Star className="h-3 w-3 text-yellow-500 mr-1" fill="currentColor" />
                    <span className="text-sm font-semibold text-white">
                      {product.reviews_rating || product.rating || 'N/A'}
                      <span className="text-xs text-gray-600 ml-1">
                        ({product.reviews_rating_count || product.reviews || 0} reviews)
                      </span>
                    </span>
                  </div>
                </div>
                <div>
                  <p className="text-xs text-gray-400">IP Complaint</p>
                  <p className="text-sm font-semibold text-white">None</p>
                </div>
              </div>

              {/* Pricing Information */}
              <div className="grid grid-cols-2 gap-3 mb-2 p-3 bg-white/5 rounded-xl border border-white/10">
                <div>
                  <p className="text-xs text-gray-400">Amazon Price (Buy Box Current)</p>
                  <p className="text-sm font-semibold text-white">
                    ${(product.amazon_price || product.buy_box_current || 0).toFixed(2)}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-400">Distributor Price</p>
                  <p className="text-sm font-semibold text-white">
                    ${(product.dprice || 0).toFixed(2)}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-400">Variation</p>
                  <p className="text-sm font-semibold text-white">No</p>
                </div>
                <div>
                  <p className="text-xs text-gray-400">Weight (lb)</p>
                  <p className="text-sm font-semibold text-white">
                    {product.weight_lb ? product.weight_lb : (product.weight ? product.weight : 'N/A')}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-400">Length (in)</p>
                  <p className="text-sm font-semibold text-white">
                    {product.length_in || 'N/A'}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-400">Width (in)</p>
                  <p className="text-sm font-semibold text-white">
                    {product.width_in || 'N/A'}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-400">Height (in)</p>
                  <p className="text-sm font-semibold text-white">
                    {product.height_in || 'N/A'}
                  </p>
                </div>
              </div>

              {/* Buy Box Information */}
              <div className="mb-2 p-3 bg-white/5 rounded-xl border border-white/10">
                <h3 className="text-base font-semibold text-white mb-2">Buy Box</h3>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div>
                    <p className="text-gray-400">3rd Party FBA Stock</p>
                    <p className="font-semibold text-white">{product.new_3rd_party_fba_stock || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-gray-400">Amazon Presence % (90d)</p>
                    <p className="font-semibold text-white">
                      {product.buy_box_amazon_90_days_pct ? `${product.buy_box_amazon_90_days_pct.toFixed(1)}%` : 'None'}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-400">Brand Presence</p>
                    <p className="font-semibold text-white">
                      {product.brand_presence ? `${(parseFloat(product.brand_presence) || 0).toFixed(1)}%` : 'None'}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-400">Number Of Sellers: </p>
                    <p className="font-semibold text-white">{product.buy_box_winner_count_90_days || 'N/A'}</p>
                  </div>
                </div>
              </div>

              {/* Fee Breakdown */}
              <div className="mb-2 p-3 bg-white/5 rounded-xl border border-white/10">
                <h3 className="text-base font-semibold text-white mb-2">Fees</h3>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div>
                    <p className="text-gray-400">FBA Fee</p>
                    <p className="font-semibold text-white">
                      ${(product.fba_fee || 0).toFixed(2)}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-400">Inbound Fee</p>
                    <p className="font-semibold text-white">
                      ${(product.inbound_fee || 0).toFixed(2)}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-400">Storage Fee</p>
                    <p className="font-semibold text-white">
                      ${(product.storage_fee === 0 ? 0.02 : (product.storage_fee || 0)).toFixed(2)}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-400">Referral Fee</p>
                    <p className="font-semibold text-white">
                      ${(product.referral_fee || 0).toFixed(2)}
                    </p>
                  </div>
                </div>
              </div>

              {/* Product Links - Moved above Purchase Button */}
              <div className="mb-4">
                <h3 className="text-base font-semibold text-white mb-2">Product Links:</h3>
                <div className="flex justify-center items-center space-x-2">
                  {/* Amazon Link */}
                  <button
                    onClick={() => hasPurchased && product.amazon_url ? window.open(product.amazon_url, '_blank') : null}
                    disabled={!hasPurchased || !product.amazon_url}
                    className={`flex-1 flex justify-center items-center px-4 py-2 rounded-lg font-medium shadow-md transition-all duration-300 text-sm w-full ${hasPurchased && product.amazon_url
                      ? 'bg-orange-600 text-white hover:bg-orange-700 hover:shadow-orange-500/20'
                      : 'bg-white/5 text-gray-400 cursor-not-allowed border border-white/10'
                      }`}
                  >
                    <span className="flex items-center justify-center w-full gap-2">
                      <Link2 className="h-4 w-4" />
                      <span>Amazon</span>
                      <div className="h-4 w-4 opacity-0" aria-hidden="true" />
                    </span>
                  </button>
                  {/* Distributor Link */}
                  <button
                    onClick={() => hasPurchased && product.dlink ? window.open(product.dlink, '_blank') : null}
                    disabled={!hasPurchased || !product.dlink}
                    className={`flex-1 flex justify-center items-center px-4 py-2 rounded-lg font-medium shadow-md transition-all duration-300 text-sm w-full ${hasPurchased && product.dlink
                      ? 'bg-indigo-600 text-white hover:bg-indigo-700 hover:shadow-indigo-500/20'
                      : 'bg-white/5 text-gray-400 cursor-not-allowed border border-white/10'
                      }`}
                  >
                    <span className="flex items-center justify-center w-full gap-2">
                      <Link2 className="h-4 w-4" />
                      <span>Distributor</span>
                      <div className="h-4 w-4 opacity-0" aria-hidden="true" />
                    </span>
                  </button>
                </div>
              </div>

              <div className="mb-4">
                <a
                  href={whatsappPurchaseLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full flex items-center justify-center py-3 rounded-xl font-bold text-base bg-gradient-to-r from-gray-800/80 to-gray-700/80 backdrop-blur-md border border-white/10 hover:from-gray-700/90 hover:to-gray-600/90 text-white transform hover:scale-[1.01] transition-all duration-300 shadow-lg shadow-gray-900/20"
                  aria-label="Purchase through WhatsApp"
                >
                  <img
                    src="https://upload.wikimedia.org/wikipedia/commons/6/6b/WhatsApp.svg"
                    alt="WhatsApp"
                    className="h-5 w-5 mr-2 brightness-0 invert"
                    loading="lazy"
                  />
                  Purchase through WhatsApp
                </a>
              </div>

              {/* Purchase Button */}
              {hasPurchased ? (
                <button
                  disabled
                  className="w-full bg-emerald-700 text-white-text py-2 rounded-lg font-bold text-base opacity-70 cursor-not-allowed flex items-center justify-center shadow-lg"
                >
                  <CheckCircle className="h-5 w-5 mr-2" /> Already Purchased
                </button>
              ) : isSoldGlobally ? (
                <button
                  disabled
                  className="w-full bg-red-600 text-white-text py-2 rounded-lg font-bold text-base opacity-70 cursor-not-allowed flex items-center justify-center shadow-lg"
                >
                  <XCircle className="h-5 w-5 mr-2" /> Sold Out
                </button>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};


// Login Page Component
const LoginPage = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const { dispatch } = useContext(AppContext);
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);


  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await login(formData.email, formData.password);
      dispatch({ type: 'ADD_NOTIFICATION', payload: { type: 'success', message: 'Login successful!' } });
      navigate('/dashboard');
    } catch (error) {
      dispatch({ type: 'ADD_NOTIFICATION', payload: { type: 'error', message: error.message } });
    }
  };

  return (
    <div className="min-h-screen bg-neutral-950 flex items-center justify-center relative overflow-hidden">
      {/* Bigger Grid Pattern */}
      <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iODAiIGhlaWdodD0iODAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI4MCIgaGVpZ2h0PSI4MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAwIDQwIEwgODAgNDAgTSA0MCAwIEwgNDAgODAiIGZpbGw9Im5vbmUiIHN0cm9rZT0icmdiYSgyNTUsMjU1LDI1NSwwLjAzKSIgc3Ryb2tlLXdpZHRoPSIxIi8+PC9wYXR0ZXJuPjwvZGVmcz48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSJ1cmwoI2dyaWQpIi8+PC9zdmc+')] opacity-50"></div>

      {/* Subtle gradient glow */}
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-blue-600 rounded-full filter blur-[150px] opacity-10"></div>
      <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-cyan-500 rounded-full filter blur-[150px] opacity-10"></div>

      <div className="w-full max-w-md px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="bg-neutral-900 rounded-2xl shadow-2xl p-8 border border-white/10">
          <h2 className="text-3xl font-bold text-center mb-8 text-white">Welcome Back</h2>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-2">
                Email
              </label>
              <input
                type="email"
                id="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="w-full px-4 py-3 bg-neutral-950 border border-white/10 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-white placeholder-gray-500"
                placeholder="Enter your email"
                required
              />
            </div>
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-300 mb-2">
                Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  id="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="w-full px-4 py-3 bg-neutral-950 border border-white/10 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-white placeholder-gray-500"
                  placeholder="Enter your password"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white transition-colors"
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>
            <button
              type="submit"
              className="w-full py-3 px-4 bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-bold rounded-xl hover:from-blue-700 hover:to-indigo-700 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:ring-offset-2 focus:ring-offset-[#0A0F1E] transition-all duration-200 shadow-lg hover:shadow-cyan-500/50"
            >
              Sign In
            </button>
          </form>
          <p className="mt-6 text-center text-gray-400">
            Don't have an account?{' '}
            <Link to="/register" className="text-indigo-400 hover:text-cyan-300 font-medium transition-colors">
              Sign up
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

// Register Page Component
const RegisterPage = () => {
  const navigate = useNavigate();
  const { register } = useAuth();
  const { dispatch } = useContext(AppContext);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    firstName: '',
    lastName: '',
    username: ''
  });
  const [showPassword, setShowPassword] = useState(false);


  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await register(formData.email, formData.password, formData.firstName, formData.lastName, formData.username);
      dispatch({ type: 'ADD_NOTIFICATION', payload: { type: 'success', message: 'Registration successful!' } });
      navigate('/dashboard');
    } catch (error) {
      dispatch({ type: 'ADD_NOTIFICATION', payload: { type: 'error', message: error.message } });
    }
  };

  return (
    <div className="min-h-screen bg-neutral-950 flex items-center justify-center relative overflow-hidden">
      {/* Bigger Grid Pattern */}
      <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iODAiIGhlaWdodD0iODAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHwiZHRoPSI4MCIgaGVpZ2h0PSI4MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAwIDQwIEwgODAgNDAgTSA0MCAwIEwgNDAgODAiIGZpbGw9Im5vbmUiIHN0cm9rZT0icmdiYSgyNTUsMjU1LDI1NSwwLjAzKSIgc3Ryb2tlLXdpZHRoPSIxIi8+PC9wYXR0ZXJuPjwvZGVmcz48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSJ1cmwoI2dyaWQpIi8+PC9zdmc+')] opacity-50"></div>

      {/* Subtle gradient glow */}
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-blue-600 rounded-full filter blur-[150px] opacity-10"></div>
      <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-cyan-500 rounded-full filter blur-[150px] opacity-10"></div>

      <div className="w-full max-w-md px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="bg-neutral-900 rounded-2xl shadow-2xl p-8 border border-white/10">
          <h2 className="text-3xl font-bold text-center mb-8 text-white">Create Account</h2>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="firstName" className="block text-sm font-medium text-gray-300 mb-2">
                  First Name
                </label>
                <input
                  type="text"
                  id="firstName"
                  value={formData.firstName}
                  onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                  className="w-full px-4 py-3 bg-neutral-950 border border-white/10 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-white placeholder-gray-500"
                  placeholder="First name"
                  required
                />
              </div>
              <div>
                <label htmlFor="lastName" className="block text-sm font-medium text-gray-300 mb-2">
                  Last Name
                </label>
                <input
                  type="text"
                  id="lastName"
                  value={formData.lastName}
                  onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                  className="w-full px-4 py-3 bg-neutral-950 border border-white/10 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-white placeholder-gray-500"
                  placeholder="Last name"
                  required
                />
              </div>
            </div>
            <div>
              <label htmlFor="username" className="block text-sm font-medium text-gray-300 mb-2">
                Username
              </label>
              <input
                type="text"
                id="username"
                value={formData.username}
                onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                className="w-full px-4 py-3 bg-neutral-950 border border-white/10 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-white placeholder-gray-500"
                placeholder="Choose a username"
                required
              />
            </div>
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-2">
                Email
              </label>
              <input
                type="email"
                id="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="w-full px-4 py-3 bg-neutral-950 border border-white/10 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-white placeholder-gray-500"
                placeholder="Enter your email"
                required
              />
            </div>
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-300 mb-2">
                Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  id="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="w-full px-4 py-3 bg-neutral-950 border border-white/10 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-white placeholder-gray-500"
                  placeholder="Create a password"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white transition-colors"
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>
            <button
              type="submit"
              className="w-full py-3 px-4 bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-bold rounded-xl hover:from-blue-700 hover:to-indigo-700 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:ring-offset-2 focus:ring-offset-[#0A0F1E] transition-all duration-200 shadow-lg hover:shadow-cyan-500/50"
            >
              Create Account
            </button>
          </form>
          <p className="mt-6 text-center text-gray-400">
            Already have an account?{' '}
            <Link to="/login" className="text-indigo-400 hover:text-cyan-300 font-medium transition-colors">
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

// Dashboard Page Component
const DashboardPage = () => {
  const { user } = useAuth();
  const { allProducts, fetchProducts, isLoading } = useProducts();
  const [showContactModal, setShowContactModal] = useState(false);
  const [contactProduct, setContactProduct] = useState(null);
  const [showRefundModal, setShowRefundModal] = useState(false); // NEW
  const [refundProduct, setRefundProduct] = useState(null); // NEW

  // Fetch all products if they aren't already loaded
  useEffect(() => {
    if (allProducts.length === 0 && !isLoading) {
      fetchProducts();
    }
  }, [allProducts, fetchProducts, isLoading]);

  if (!user || isLoading) { // Check for both user and product loading states
    return (
      <div className="min-h-screen flex items-center justify-center text-xl text-gray-400 animate-pulse bg-neutral-950">
        Loading dashboard data...
      </div>
    );
  }

  const purchasedProductDetails = user.purchasedProducts
    .map(productId => allProducts.find(p => p.id === productId))
    .filter(Boolean);

  const handleContactClick = (product) => {
    setContactProduct(product); // Set the product for which contact is requested
    setShowContactModal(true);
  };

  const handleCloseContactModal = () => {
    setShowContactModal(false);
    setContactProduct(null); // Clear product on close
  };

  return (
    <div className="min-h-screen bg-neutral-950 text-white font-sans selection:bg-indigo-500/30 pt-24 pb-12 relative overflow-hidden">
      {/* Clean gradient */}
      <div className="absolute inset-0 bg-gradient-to-b from-neutral-950 via-neutral-900 to-neutral-950 opacity-30"></div>

      {/* Enhanced visible grid */}
      <div className="absolute inset-0 opacity-[0.12]" style={{
        backgroundImage: `url("data:image/svg+xml,%3Csvg width='80' height='80' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M 80 0 L 0 0 0 80' fill='none' stroke='rgba(100,180,255,0.3)' stroke-width='1'/%3E%3C/svg%3E")`,
        backgroundSize: '80px 80px'
      }}></div>



      <div className={`relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 ${showContactModal || showRefundModal ? 'filter blur-sm' : ''}`}>
        <h1 className="text-4xl font-extrabold text-white-text mb-8">Hello, <span className="bg-gradient-to-r from-blue-400 via-indigo-500 to-violet-500 bg-clip-text text-transparent">{user.username}</span>!</h1>
        <p className="text-lg text-light-gray-text mb-6">This is your personalized dashboard.</p>


        <div className="bg-neutral-900 backdrop-blur-xl p-6 rounded-lg shadow-xl border border-white/10">
          <h2 className="text-2xl font-semibold text-white mb-4 flex items-center"><ShoppingCart className="mr-2 text-indigo-400" /> Your Purchased Products</h2>
          {purchasedProductDetails.length > 0 ? (
            <ul
              className={`space-y-4 ${purchasedProductDetails.length > 5 ? 'max-h-96 overflow-y-auto pr-2' : ''}`}
              style={purchasedProductDetails.length > 5 ? { scrollbarGutter: 'stable' } : {}}
            >
              {purchasedProductDetails.map(product => (
                <li key={product.id} className="flex flex-col sm:flex-row items-start sm:items-center justify-between p-4 bg-white/5 backdrop-blur-md rounded-xl border border-white/10 hover:border-indigo-500/30 transition-all duration-300">
                  <div className="flex items-center mb-2 sm:mb-0">
                    <div className="w-16 h-16 flex items-center justify-center rounded-lg mr-4 border border-indigo-500/20 bg-black/20">
                      <img src={product.imageUrl} alt={product.title} className="w-12 h-12 object-cover rounded-md" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg text-white truncate">{product.title.split(' ')[0] + (product.title.split(' ').length > 1 ? '...' : '')}</h3>
                      <p className="text-white text-sm font-bold" style={{ textShadow: '-0.5px -0.5px 0 #000, 0.5px -0.5px 0 #000, -0.5px 0.5px 0 #000, 0.5px 0.5px 0 #000, 2px 2px 0 #000' }}>${product.price.toFixed(2)}</p>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2 justify-end">
                    <Link to={`/products/${product.id}`} className="text-gray-300 hover:text-white flex items-center transition-colors duration-200 px-3 py-1.5 rounded-lg bg-white/5 hover:bg-white/10 border border-white/5">
                      View Details <ChevronRight className="h-4 w-4 ml-1" />
                    </Link>
                    <button onClick={() => handleContactClick(product)} className="bg-gradient-to-r from-indigo-900 to-blue-900 text-white px-3 py-1.5 rounded-lg hover:from-indigo-800 hover:to-blue-800 transition-all duration-200 shadow-lg shadow-indigo-900/20">Contact</button>
                    <button onClick={() => { setRefundProduct(product); setShowRefundModal(true); }} className="bg-gradient-to-r from-red-900 to-pink-900 text-white px-3 py-1.5 rounded-lg hover:from-red-800 hover:to-pink-800 transition-all duration-200 shadow-lg shadow-red-900/20">Refund</button>
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-gray-400">You haven't purchased any products yet.</p>
          )}
        </div>
      </div>
      <ContactModal
        isOpen={showContactModal}
        onClose={() => { setShowContactModal(false); setContactProduct(null); }}
        recipientEmail="muhamed.ahmed100@gmail.com"
        link={contactProduct?.amazon_url || contactProduct?.amazonUrl || ''}
        reason="query"
      />
      <RefundModal
        isOpen={showRefundModal}
        onClose={() => { setShowRefundModal(false); setRefundProduct(null); }}
        recipientEmail="muhamed.ahmed100@gmail.com"
        link={refundProduct?.amazon_url || refundProduct?.amazonUrl || ''}
        reason="refund"
      />
    </div>
  );
};

// Admin Page Component (Placeholder)
const AdminPage = () => {
  const { user } = useAuth();
  const { allProducts } = useProducts();

  if (!user || !user.isAdmin) {
    return <Navigate to="/login" />;
  }

  return (
    <div className="min-h-screen bg-background-black text-white-text pt-24 pb-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="text-4xl font-extrabold text-white-text mb-8">Admin Panel</h1>
        <p className="text-lg text-light-gray-text mb-6">Manage application data and users.</p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <div className="bg-dark-gray p-6 rounded-lg shadow-md border border-teal-accent-700/50">
            <h2 className="text-2xl font-semibold text-white-text mb-3 flex items-center"><Users className="mr-2 bg-gradient-to-r from-[#38BDF8] to-[#06B6D4] bg-clip-text text-transparent" /> User Management</h2>
            <p className="text-light-gray-text">View and manage user accounts.</p>
            <button className="mt-4 bg-teal-accent-600 text-white-text px-4 py-2 rounded-md hover:bg-teal-accent-700 transition-colors duration-300 shadow-md">View Users</button>
          </div>
          <div className="bg-dark-gray p-6 rounded-lg shadow-md border border-teal-accent-700/50">
            <h2 className="text-2xl font-semibold text-white-text mb-3 flex items-center"><Package className="mr-2 bg-gradient-to-r from-[#38BDF8] to-[#06B6D4] bg-clip-text text-transparent" /> Product Management</h2>
            <p className="text-light-gray-text">Add, edit, or delete products.</p>
            <button className="mt-4 bg-teal-accent-600 text-white-text px-4 py-2 rounded-md hover:bg-teal-accent-700 transition-colors duration-300 shadow-md">Manage Products</button>
          </div>
        </div>

        <div className="bg-dark-gray p-6 rounded-lg shadow-md border border-teal-accent-700/50">
          <h2 className="text-2xl font-semibold text-white-text mb-4 flex items-center"><BarChart3 className="mr-2 bg-gradient-to-r from-[#38BDF8] to-[#06B6D4] bg-clip-text text-transparent" /> System Metrics</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-light-gray-text">
            <p>Total Products: <span className="font-semibold text-white-text">{allProducts.length}</span></p>
            <p>Active Users: <span className="font-semibold text-white-text">2 (mock)</span></p>
            <p>API Calls Today: <span className="font-semibold text-white-text">150 (mock)</span></p>
            <p>Revenue (Monthly): <span className="font-semibold text-white-text">$5,000 (mock)</span></p>
          </div>
        </div>
      </div>
    </div>
  );
};

// About Us Page Component (Placeholder)
const AboutPage = () => {

  return (
    <div className="min-h-screen bg-neutral-950 relative overflow-hidden">
      {/* Bigger Grid Pattern */}
      <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iODAiIGhlaWdodD0iODAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI4MCIgaGVpZ2h0PSI4MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAwIDQwIEwgODAgNDAgTSA0MCAwIEwgNDAgODAiIGZpbGw9Im5vbmUiIHN0cm9rZT0icmdiYSgyNTUsMjU1LDI1NSwwLjAzKSIgc3Ryb2tlLXdpZHRoPSIxIi8+PC9wYXR0ZXJuPjwvZGVmcz48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSJ1cmwoI2dyaWQpIi8+PC9zdmc+')] opacity-50"></div>



      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 pt-32">
        <div className="text-center mb-16">
          <h1 className="text-5xl font-black mb-4 text-white">
            About <span className="bg-gradient-to-r from-blue-400 via-indigo-500 to-violet-500 bg-clip-text text-transparent">AMZProdHunter</span>
          </h1>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto">
            Your trusted partner in product research and market analysis
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
          {/* Mission Section */}
          <div className="bg-neutral-900 rounded-2xl border border-white/10 p-8 shadow-2xl">
            <h2 className="text-2xl font-bold text-white mb-6">Our Mission</h2>
            <p className="text-gray-400 leading-relaxed">
              At AMZProdHunter, we're dedicated to empowering businesses with real-time market insights and competitive intelligence. Our platform helps you make data-driven decisions by providing comprehensive product analysis and market trends.
            </p>
          </div>

          {/* Vision Section */}
          <div className="bg-neutral-900 rounded-2xl border border-white/10 p-8 shadow-2xl">
            <h2 className="text-2xl font-bold text-white mb-6">Our Vision</h2>
            <p className="text-gray-400 leading-relaxed">
              We envision a world where every business has access to accurate, timely, and actionable market intelligence. Through our innovative platform, we're making product research accessible, efficient, and insightful for businesses of all sizes.
            </p>
          </div>

          {/* Values Section */}
          <div className="bg-neutral-900 rounded-2xl border border-white/10 p-8 shadow-2xl">
            <h2 className="text-2xl font-bold text-white mb-6">Our Values</h2>
            <ul className="space-y-4 text-gray-400">
              <li className="flex items-start">
                <span className="text-indigo-400 mr-2">•</span>
                <span>Innovation in market research and analysis</span>
              </li>
              <li className="flex items-start">
                <span className="text-indigo-400 mr-2">•</span>
                <span>Data-driven decision making</span>
              </li>
              <li className="flex items-start">
                <span className="text-indigo-400 mr-2">•</span>
                <span>Customer success and satisfaction</span>
              </li>
              <li className="flex items-start">
                <span className="text-indigo-400 mr-2">•</span>
                <span>Continuous improvement and learning</span>
              </li>
            </ul>
          </div>

          {/* Team Section */}
          <div className="bg-neutral-900 rounded-2xl border border-white/10 p-8 shadow-2xl">
            <h2 className="text-2xl font-bold text-white mb-6">Our Team</h2>
            <p className="text-gray-400 leading-relaxed mb-4">
              Our team consists of experienced market analysts, data scientists, and product experts who are passionate about helping businesses succeed in today's competitive market.
            </p>
            <div className="flex justify-center mt-6">
              <div className="text-center">
                <div className="w-20 h-20 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-full mx-auto mb-3"></div>
                <h3 className="text-white font-semibold">Muhammed Ahmed</h3>
                <p className="text-gray-400 text-sm">CEO & Founder</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Contact Page Component (Placeholder)
const ContactPage = () => {

  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '' });
  const [isSending, setIsSending] = useState(false);
  const { dispatch } = useContext(AppContext);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.id]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.subject || !form.message) {
      dispatch({ type: 'ADD_NOTIFICATION', payload: { type: 'error', message: 'Please fill in all fields.' } });
      return;
    }
    setIsSending(true);
    try {
      const serviceID = process.env.REACT_APP_EMAILJS_SERVICE_ID;
      const templateID = process.env.REACT_APP_EMAILJS_TEMPLATE_ID2;
      const publicKey = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;
      if (!serviceID || !templateID || !publicKey) {
        throw new Error('Email.js environment variables are not set. Please check your .env file and restart the server.');
      }
      const templateParams = {
        Name: form.name,
        Email: form.email,
        Subject: form.subject,
        message: form.message,
        to_email: 'muhamed.ahmed100@gmail.com',
      };
      await emailjs.send(serviceID, templateID, templateParams, publicKey);
      dispatch({ type: 'ADD_NOTIFICATION', payload: { type: 'success', message: 'Your message has been sent successfully!' } });
      setForm({ name: '', email: '', subject: '', message: '' });
    } catch (error) {
      console.error('Error sending email via Email.js:', error);
      dispatch({ type: 'ADD_NOTIFICATION', payload: { type: 'error', message: `Failed to send message: ${error.message}` } });
    } finally {
      setIsSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-neutral-950 text-white relative overflow-hidden flex items-center justify-center pt-24 pb-12">
      {/* Bigger Grid Pattern */}
      <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iODAiIGhlaWdodD0iODAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI4MCIgaGVpZ2h0PSI4MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAwIDQwIEwgODAgNDAgTSA0MCAwIEwgNDAgODAiIGZpbGw9Im5vbmUiIHN0cm9rZT0icmdiYSgyNTUsMjU1LDI1NSwwLjAzKSIgc3Ryb2tlLXdpZHRoPSIxIi8+PC9wYXR0ZXJuPjwvZGVmcz48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSJ1cmwoI2dyaWQpIi8+PC9zdmc+')] opacity-50"></div>



      {/* Content */}
      <div className="relative z-10 max-w-xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
        <div className="bg-neutral-900 rounded-2xl shadow-2xl p-8 border border-white/10">
          <h1 className="text-4xl font-bold text-white mb-6 text-center">
            Contact Us
          </h1>
          <p className="text-lg text-gray-400 mb-8 text-center">
            Have questions, feedback, or need support? Reach out to us!
          </p>
          <form className="space-y-6" onSubmit={handleSubmit}>
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-white-text mb-2">Your Name</label>
              <input
                type="text"
                id="name"
                value={form.name}
                onChange={handleChange}
                className="block w-full px-4 py-2 border border-teal-accent-900 rounded-md shadow-sm focus:ring-teal-accent-500 focus:border-teal-accent-500 bg-white/20 backdrop-blur-md text-white placeholder-white transition-all duration-200"
                placeholder="John Doe"
                required
              />
            </div>
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-white-text mb-2">Your Email</label>
              <input
                type="email"
                id="email"
                value={form.email}
                onChange={handleChange}
                className="block w-full px-4 py-2 border border-teal-accent-900 rounded-md shadow-sm focus:ring-teal-accent-500 focus:border-teal-accent-500 bg-white/20 backdrop-blur-md text-white placeholder-white transition-all duration-200"
                placeholder="you@example.com"
                required
              />
            </div>
            <div>
              <label htmlFor="subject" className="block text-sm font-medium text-white-text mb-2">Subject</label>
              <input
                type="text"
                id="subject"
                value={form.subject}
                onChange={handleChange}
                className="block w-full px-4 py-2 border border-teal-accent-900 rounded-md shadow-sm focus:ring-teal-accent-500 focus:border-teal-accent-500 bg-white/20 backdrop-blur-md text-white placeholder-white transition-all duration-200"
                placeholder="Regarding a product / Support request"
                required
              />
            </div>
            <div>
              <label htmlFor="message" className="block text-sm font-medium text-white-text mb-2">Your Message</label>
              <textarea
                id="message"
                rows="5"
                value={form.message}
                onChange={handleChange}
                className="block w-full px-4 py-2 border border-teal-accent-900 rounded-md shadow-sm focus:ring-teal-accent-500 focus:border-teal-accent-500 bg-white/20 backdrop-blur-md text-white placeholder-white transition-all duration-200"
                placeholder="Type your message here..."
                required
              ></textarea>
            </div>
            <button
              type="submit"
              disabled={isSending}
              className="w-full py-3 px-4 bg-gradient-to-r from-indigo-900 to-blue-900 text-white rounded-xl font-bold text-lg transition-all duration-300 hover:from-indigo-700 hover:to-blue-700 shadow-lg shadow-indigo-900/20 hover:shadow-indigo-500/40 hover:-translate-y-1 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSending ? 'Sending...' : 'Send Message'}
            </button>
          </form>
          <div className="mt-8 text-center text-light-gray-text">
            <p className="flex items-center justify-center mb-2"><Mail className="h-5 w-5 mr-2" /> Email: <a href="mailto:muhamed.ahmed100@gmail.com" className="ml-1 hover:underline">muhamed.ahmed100@gmail.com</a></p>
            <p className="flex items-center justify-center"><Info className="h-5 w-5 mr-2" /> Address: Zakariya Hostel NUST, Islamabad</p>
          </div>
        </div>
      </div>
    </div>
  );
};

// Email Confirmation Page Component
const EmailConfirmationPage = () => {
  const [email, setEmail] = useState('');
  const [isResending, setIsResending] = useState(false);
  const { dispatch } = useContext(AppContext);

  useEffect(() => {
    // Get email from URL params or localStorage
    const urlParams = new URLSearchParams(window.location.search);
    const emailFromUrl = urlParams.get('email');
    if (emailFromUrl) {
      setEmail(emailFromUrl);
    } else {
      // Attempt to get email from Firebase Auth if already logged in (e.g., after initial sign-up and redirect)
      const currentUser = auth.currentUser;
      if (currentUser) {
        setEmail(currentUser.email);
      }
    }
  }, []);

  const handleResendEmail = async () => {
    if (!email) {
      dispatch({ type: 'ADD_NOTIFICATION', payload: { type: 'error', message: 'Please enter your email address to resend.' } });
      return;
    }

    try {
      setIsResending(true);
      // Firebase Auth doesn't have a direct 'resend' method like Supabase for signup.
      // The typical flow is to get the current user and send verification email.
      // If the user isn't logged in, they need to sign in first, then resend.
      // For this page, we'll assume the email field is either pre-filled or entered.
      // If the user is not authenticated, this call won't work directly.
      // For a robust solution, you might need to combine this with a re-authentication step or handle it on the registration page after signup.

      // To resend a verification email in Firebase, you need an authenticated user.
      // If the user is on this page *after* a fresh registration, auth.currentUser will be available.
      const currentUser = auth.currentUser;

      if (currentUser && currentUser.email === email && !currentUser.emailVerified) {
        await sendEmailVerification(currentUser);
        dispatch({ type: 'ADD_NOTIFICATION', payload: { type: 'success', message: 'Verification email sent! Please check your inbox.' } });
      } else if (currentUser && currentUser.emailVerified) {
        dispatch({ type: 'ADD_NOTIFICATION', payload: { type: 'info', message: 'Your email is already verified.' } });
      } else {
        // If no current user or email mismatch, it means they might have closed the browser, etc.
        // In a real app, you might want to prompt them to log in again or go back to register.
        dispatch({ type: 'ADD_NOTIFICATION', payload: { type: 'error', message: 'Could not resend email. Please try logging in first, or ensure you are using the correct email.' } });
      }

    } catch (error) {
      console.error('Error resending Firebase verification email:', error);
      dispatch({ type: 'ADD_NOTIFICATION', payload: { type: 'error', message: error.message } });
    } finally {
      setIsResending(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-teal-accent-950 via-background-black to-teal-accent-950 flex items-center justify-center relative overflow-hidden">
      {/* Background Bubbles */}
      <div className="absolute inset-0 z-0">
        <div className="absolute w-64 h-64 bg-teal-accent-100 rounded-full opacity-20 animate-float-blur-1 top-[10%] left-[5%] shadow-[0_0_80px_25px_rgba(56,189,248,0.3)]"></div>
        <div className="absolute w-48 h-48 bg-teal-accent-300 rounded-full opacity-15 animate-float-blur-2 delay-100 bottom-[20%] left-[8%] shadow-[0_0_50px_15px_rgba(56,189,248,0.2)]"></div>
        <div className="absolute w-72 h-72 bg-teal-accent-600 rounded-full opacity-10 animate-float-blur-3 delay-200 top-[40%] left-[2%] shadow-[0_0_30px_8px_rgba(56,189,248,0.15)]"></div>
      </div>


      <div className="w-full max-w-md px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="bg-gradient-to-br from-teal-accent-950 to-background-black rounded-lg shadow-xl p-8 border border-teal-accent-700/50">
          <div className="text-center mb-8">
            <div className="bg-teal-accent-500 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
              <Mail className="h-8 w-8 text-white-text" />
            </div>
            <h2 className="text-3xl font-bold text-white-text mb-4">Check Your Email</h2>
            <p className="text-light-gray-text">
              We've sent a confirmation link to your email address. Please click the link to activate your account.
            </p>
          </div>

          <div className="space-y-6">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-2">
                Email Address
              </label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-3 bg-neutral-950 border border-white/10 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-white placeholder-gray-500"
                placeholder="Enter your email address"
              />
            </div>

            <button
              onClick={handleResendEmail}
              disabled={isResending}
              className="w-full py-3 px-4 bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-bold rounded-xl hover:from-blue-700 hover:to-indigo-700 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:ring-offset-2 focus:ring-offset-[#0A0F1E] transition-all duration-200 shadow-lg hover:shadow-cyan-500/50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isResending ? 'Sending...' : 'Resend Confirmation Email'}
            </button>

            <div className="text-center">
              <Link to="/login" className="text-indigo-400 hover:text-teal-accent-300 font-medium transition-colors">
                Back to Login
              </Link>
            </div>
          </div>

          <div className="mt-8 p-4 bg-teal-accent-900/20 rounded-lg border border-teal-accent-700/30">
            <h3 className="text-sm font-semibold text-white-text mb-2">Need Help?</h3>
            <ul className="text-xs text-light-gray-text space-y-1">
              <li>• Check your spam/junk folder</li>
              <li>• Make sure you entered the correct email address</li>
              <li>• Wait a few minutes for the email to arrive</li>
              <li>• Click the confirmation link in the email</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

// Contact Modal Component
const ContactModal = ({ isOpen, onClose, recipientEmail, link, reason }) => {
  const { user } = useAuth();
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [isSending, setIsSending] = useState(false);
  const { dispatch } = useContext(AppContext);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!subject || !message) {
      dispatch({ type: 'ADD_NOTIFICATION', payload: { type: 'error', message: 'Please fill in all fields.' } });
      return;
    }

    setIsSending(true);
    try {
      const serviceID = process.env.REACT_APP_EMAILJS_SERVICE_ID;
      const templateID = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
      const publicKey = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;

      if (!serviceID || !templateID || !publicKey) {
        throw new Error('Email.js environment variables are not set. Please check your .env file and restart the server.');
      }

      const templateParams = {
        subject: subject,
        message: message,
        user_email: user?.email || 'anonymous@example.com',
        user_name: user?.username || 'Anonymous User',
        to_email: recipientEmail,
        link: link || '',
        reason: reason || '', // Pass reason to template
      };
      console.log('EmailJS templateParams:', templateParams);
      await emailjs.send(serviceID, templateID, templateParams, publicKey);

      dispatch({ type: 'ADD_NOTIFICATION', payload: { type: 'success', message: 'Your message has been sent successfully!' } });
      setSubject('');
      setMessage('');
      onClose();
    } catch (error) {
      console.error('Error sending email via Email.js:', error);
      dispatch({ type: 'ADD_NOTIFICATION', payload: { type: 'error', message: `Failed to send message: ${error.message}` } });
    } finally {
      setIsSending(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50 backdrop-blur-sm">
      <div className="bg-gradient-to-br from-teal-accent-950 to-background-black rounded-lg shadow-xl p-6 w-full max-w-md border border-teal-accent-700/50 relative transform animate-scale-in">
        <button
          onClick={onClose}
          className="absolute top-3 right-3 text-light-gray-text hover:text-white transition-colors duration-200"
          aria-label="Close"
        >
          <X className="h-6 w-6" />
        </button>
        <h2 className="text-2xl font-bold text-white-text mb-6 text-center">Contact Support</h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="subject" className="block text-sm font-medium text-gray-300 mb-2">Subject</label>
            <input
              type="text"
              id="subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              className="w-full px-4 py-2 bg-background-black/80 border border-teal-accent-900/30 rounded-lg focus:ring-2 focus:ring-teal-accent-500 focus:border-transparent text-white placeholder-gray-400"
              placeholder="Enter subject"
              required
            />
          </div>
          <div>
            <label htmlFor="message" className="block text-sm font-medium text-gray-300 mb-2">Your Message</label>
            <textarea
              id="message"
              rows="5"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              className="w-full px-4 py-2 bg-background-black/80 border border-teal-accent-900/30 rounded-lg focus:ring-2 focus:ring-teal-accent-500 focus:border-transparent text-white placeholder-gray-400"
              placeholder="Write your query here..."
              required
            ></textarea>
          </div>
          {link && (
            <div className="text-xs text-light-gray-text mb-2">Amazon Link: <a href={link} target="_blank" rel="noopener noreferrer" className="text-cyan-400 underline break-all">{link}</a></div>
          )}
          <button
            type="submit"
            disabled={isSending}
            className="w-full py-2 px-4 bg-gradient-to-r from-teal-accent-600 to-teal-accent-700 text-white font-medium rounded-lg hover:from-teal-accent-700 hover:to-teal-accent-800 focus:outline-none focus:ring-2 focus:ring-teal-accent-500 focus:ring-offset-2 focus:ring-offset-background-black transition-all duration-200 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSending ? 'Sending...' : 'Send Email'}
          </button>
        </form>
      </div>
    </div>
  );
};

// Refund Modal is just ContactModal with a different reason
const RefundModal = (props) => <ContactModal {...props} />;

// App Provider Wrapper
const AppProvider = ({ children }) => {
  const [state, dispatch] = useReducer(appReducer, initialState);
  const [sessionLoading, setSessionLoading] = useState(true);

  useEffect(() => {
    console.log('[AppProvider] useEffect: Setting up Firebase Auth listener');

    const unsubscribe = onAuthStateChanged(auth, async (userAuth) => {
      console.log('[AppProvider] onAuthStateChanged event. User:', userAuth);

      try {
        if (userAuth) {
          // User is signed in, fetch custom user data from Firestore
          console.log('[AppProvider] User is logged in, fetching custom data...');

          let customUserData = {};
          let purchasedProductIds = [];

          try {
            const userDocRef = doc(db, TABLES.USERS, userAuth.uid);
            const userDocSnap = await getDoc(userDocRef);
            customUserData = userDocSnap.exists() ? userDocSnap.data() : {};
            console.log('[AppProvider] Custom user data:', customUserData);

            // Also fetch purchased products
            purchasedProductIds = await APIService.fetchUserPurchases(userAuth.uid);
            console.log('[AppProvider] Purchased products:', purchasedProductIds);
          } catch (firestoreError) {
            console.warn('[AppProvider] Firestore data fetch failed, using defaults:', firestoreError);
            // Use defaults if Firestore fails
            customUserData = {};
            purchasedProductIds = [];
          }

          dispatch({
            type: 'SET_USER',
            payload: {
              id: userAuth.uid,
              email: userAuth.email,
              username: customUserData?.username || userAuth.email.split('@')[0],
              isAdmin: customUserData?.is_admin || false,
              purchasedProducts: purchasedProductIds,
              tokenUsage: customUserData?.token_usage || 0,
              maxTokens: customUserData?.max_tokens || 100
            }
          });
        } else {
          // User is signed out
          console.log('[AppProvider] User is logged out, setting user to null');
          dispatch({ type: 'SET_USER', payload: null });
        }
      } catch (error) {
        console.error('[AppProvider] Error in auth state change:', error);
        // On error, set user to null for safety
        dispatch({ type: 'SET_USER', payload: null });
      } finally {
        // Once initial auth state is determined, set loading to false
        setSessionLoading(false);
        console.log('[AppProvider] Session loading complete.');
      }
    });

    // Clean up subscription on unmount
    return () => {
      console.log('[AppProvider] Unsubscribing from Firebase Auth listener.');
      unsubscribe();
    };
  }, [dispatch]); // dispatch is stable

  useEffect(() => {
    if (state.darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [state.darkMode]);

  const removeNotification = (id) => {
    dispatch({ type: 'REMOVE_NOTIFICATION', payload: id });
  };

  if (sessionLoading) {
    return (
      <div className="fixed inset-0 bg-neutral-950 flex items-center justify-center z-[9999]" style={{
        animation: 'fadeIn 0.5s ease-out'
      }}>
        {/* Subtle grid background */}
        <div className="absolute inset-0 opacity-[0.05]" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='80' height='80' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M 80 0 L 0 0 0 80' fill='none' stroke='rgba(99,102,241,0.3)' stroke-width='1'/%3E%3C/svg%3E")`,
          backgroundSize: '80px 80px'
        }}></div>

        {/* Logo container */}
        <div className="relative z-10 flex flex-col items-center" style={{
          animation: 'fadeInUp 0.8s ease-out 0.2s both'
        }}>
          {/* Subtle static glow */}
          <div className="absolute inset-0 -m-20 bg-indigo-500/15 blur-[80px] rounded-full"></div>

          {/* Tech Rings Loader */}
          <div className="relative mb-8">
            <div className="relative w-24 h-24">
              {/* Ambient Glow */}
              <div className="absolute inset-0 bg-indigo-500/20 blur-xl rounded-full animate-pulse"></div>

              {/* Outer Ring */}
              <div className="absolute inset-0 border-2 border-t-indigo-500 border-r-transparent border-b-purple-500 border-l-transparent rounded-full animate-spin" style={{ animationDuration: '3s' }}></div>

              {/* Inner Ring */}
              <div className="absolute inset-3 border-2 border-r-indigo-400 border-t-transparent border-l-purple-400 border-b-transparent rounded-full animate-spin" style={{ animationDuration: '1.5s', animationDirection: 'reverse' }}></div>

              {/* Center Core */}
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-2 h-2 bg-white rounded-full shadow-[0_0_10px_rgba(255,255,255,0.8)] animate-pulse"></div>
              </div>
            </div>
          </div>

          {/* Simple loading text */}
          <p className="text-white text-lg font-medium tracking-wider">Loading...</p>
        </div>

        {/* Simple animations */}
        <style>{`
          @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }
          
          @keyframes fadeInUp {
            from {
              opacity: 0;
              transform: translateY(20px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
        `}</style>
      </div>
    );
  }

  return (
    <AppContext.Provider value={{ state, dispatch, sessionLoading }}>
      {children}
      <div className="fixed top-20 right-4 z-50 space-y-3">
        {state.notifications.map(notification => (
          <Notification
            key={notification.id}
            notification={notification}
            onRemove={removeNotification}
          />
        ))}
      </div>
    </AppContext.Provider>
  );
};

// ScrollToTop component to handle scroll restoration on route change
const ScrollToTop = () => {
  const location = useLocation();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [location.pathname]);

  return null;
};

// Main App Component
const App = () => {
  return (
    <Router>
      <AppProvider>
        <div className="relative flex flex-col min-h-screen bg-[#0A0F1E] font-sans overflow-hidden">
          {/* Global background with enhanced effects */}
          <div className="fixed inset-0 pointer-events-none z-0 overflow-hidden">
            {/* Enhanced lighting - Ultra smooth */}
            <div className="absolute top-0 left-1/4 w-[700px] h-[700px] rounded-full filter blur-[150px] animate-pulse" style={{ background: 'radial-gradient(circle, rgba(59,130,246,0.10) 0%, rgba(59,130,246,0.05) 40%, transparent 70%)', animationDuration: '4s' }}></div>
            <div className="absolute bottom-0 right-1/4 w-[700px] h-[700px] rounded-full filter blur-[150px] animate-pulse" style={{ background: 'radial-gradient(circle, rgba(6,182,212,0.10) 0%, rgba(6,182,212,0.05) 40%, transparent 70%)', animationDelay: '1.5s', animationDuration: '4s' }}></div>

            {/* More visible grid */}
            <div className="absolute inset-0 opacity-[0.08]" style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='80' height='80' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M 80 0 L 0 0 0 80' fill='none' stroke='rgba(100,180,255,0.3)' stroke-width='1'/%3E%3C/svg%3E")`,
              backgroundSize: '80px 80px'
            }}></div>
          </div>

          {/* Content wrapper with higher z-index */}
          <div className="relative z-10 flex flex-col min-h-screen">
            <ScrollToTop />
            <NavBar />
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/products" element={<ProductsPage />} />
              <Route path="/products/:id" element={<ProductDetailPage />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/register" element={<RegisterPage />} />
              <Route path="/confirm-email" element={<EmailConfirmationPage />} />
              <Route path="/dashboard" element={<DashboardPage />} />
              <Route path="/admin" element={<AdminPage />} />
              <Route path="/about" element={<AboutPage />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/privacy" element={<PrivacyPolicyPage />} />
              <Route path="/security" element={<SecurityPolicyPage />} />
              <Route path="/terms" element={<TermsPage />} />
              <Route path="/refunds" element={<RefundPolicyPage />} />
              <Route path="/shipping" element={<ShippingPolicyPage />} />
              <Route path="/payment-policy" element={<PaymentPolicyPage />} />
              <Route path="*" element={<Navigate to="/" />} />
            </Routes>
            <Footer />
          </div>
          <SpeedInsights />
        </div>
      </AppProvider>
    </Router>
  );
};

export default App;
